import { gql } from '@apollo/client';

export const LOGIN_MUTATION = gql`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password)
  }
`;

export const FORGOT_PASSWORD_MUTATION = gql`
  mutation ForgotPassword($identifier: String!) {
    forgotPassword(identifier: $identifier)
  }
`;

export const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPassword($token: String!, $newPassword: String!) {
    resetPassword(token: $token, newPassword: $newPassword)
  }
`;

export const SIGNUP_MUTATION = gql`
  mutation Signup($input: NewUserInput!) {
    signup(input: $input)
  }
`;

export const LIST_OF_VALUES_QUERY = gql`
  query ListOfValues($category: String!) {
    listOfValues(category: $category) {
      value
      label
      sortOrder
    }
  }
`;

export const FAMILY_USERS_QUERY = gql`
  query FamilyUsers {
    familyUsers {
      id email roleName roleDisplayName displayName isActive serviceCategories
      wishlistCount wishlistStatus eventDate matchmakerEmail
      accountManager { userId name email phone roleName source endsOn assignedAt active }
      pagePermissions { page canRead canCreate canUpdate canDelete }
    }
  }
`;

export const ME_QUERY = gql`
  query Me {
    me {
      id
      email
      phone
      isVerified
      menuVisibility
      role { id name displayName }
      permissions
      pagePermissions { page canRead canCreate canUpdate canDelete }
      profile {
        id profileFor firstName lastName gender dob
        motherTongue religion community caste subCaste
        heightCm weightKg physicalStatus maritalStatus
        education profession employmentType annualIncome
        familyBackground aboutMe location
        country state city pincode
        horoscopeStar horoscopeRasi horoscopeGothram
        birthTime birthPlace birthUtcOffset horoscopeUrl matrimonyDeclaredAt
        photoUrl profileCompletion
      }
    }
  }
`;

export const CREATE_PROFILE_MUTATION = gql`
  mutation CreateProfile($input: ProfileInput!) {
    createProfile(input: $input) {
      id firstName lastName profileCompletion
    }
  }
`;

export const UPDATE_PROFILE_MUTATION = gql`
  mutation UpdateProfile($input: ProfileInput!) {
    updateProfile(input: $input) {
      id firstName lastName profileCompletion birthTime horoscopeUrl
    }
  }
`;

export const MATCHES_QUERY = gql`
  query Matches($community: String, $religion: String, $city: String, $state: String, $minAge: Int, $maxAge: Int, $gender: String, $maritalStatus: String, $motherTongue: String) {
    matches(community: $community, religion: $religion, city: $city, state: $state, minAge: $minAge, maxAge: $maxAge, gender: $gender, maritalStatus: $maritalStatus, motherTongue: $motherTongue) {
      userId firstName lastName age gender community religion motherTongue
      education profession employmentType annualIncome
      city state country location heightCm
      maritalStatus aboutMe horoscopeStar horoscopeRasi photoUrl
      interestStatus isShortlisted compatibilityScore verified
    }
  }
`;

export const SENT_INTERESTS_QUERY = gql`
  query SentInterests {
    sentInterests {
      id status note createdAt
      receiver { id email profile { firstName lastName city state community photoUrl } }
    }
  }
`;

export const RECEIVED_INTERESTS_QUERY = gql`
  query ReceivedInterests {
    receivedInterests {
      id status note createdAt
      sender { id email profile { firstName lastName city state community photoUrl } }
    }
  }
`;

export const SHORTLISTED_QUERY = gql`
  query Shortlisted {
    shortlisted {
      userId firstName lastName age community city state location profession photoUrl
      interestStatus isShortlisted
    }
  }
`;

export const DASHBOARD_STATS_QUERY = gql`
  query DashboardStats {
    dashboardStats {
      profileViews sentInterests receivedInterests acceptedInterests
      shortlisted newMatches profileCompletion
    }
  }
`;

export const SEND_INTEREST_MUTATION = gql`
  mutation SendInterest($toUserId: ID!, $note: String) {
    sendInterest(toUserId: $toUserId, note: $note) { id status }
  }
`;

export const RESPOND_INTEREST_MUTATION = gql`
  mutation RespondInterest($interestId: ID!, $status: String!) {
    respondInterest(interestId: $interestId, status: $status) { id status }
  }
`;

export const TOGGLE_SHORTLIST_MUTATION = gql`
  mutation ToggleShortlist($targetId: ID!) {
    toggleShortlist(targetId: $targetId)
  }
`;

export const CONTRACTS_QUERY = gql`
  query Contracts {
    contracts {
      id refNumber title body terms status penaltyClause cost costDescription dueDate createdAt
      templateId assignedToUser reviewNotes canEdit
      signedAt signatureData
      userSignatureData userSignedAt acceptedAt
      creator { id email }
      assignedUser { id email role { name displayName } profile { firstName lastName } }
      parties { type name vendorName brideName groomName label }
      template { id name category }
      milestones { id stageName amount dueDate paidAt notes createdAt }
      serviceItems { id serviceCategory serviceName }
    }
  }
`;

// The party (vendor / bride or groom family) or the family's matchmaker edits a contract before it
// is fully signed; it goes back to the admin for review (agreement and signatures are cleared)
export const EDIT_CONTRACT_AS_PARTY_MUTATION = gql`
  mutation EditContractAsParty($id: ID!, $input: PartyContractEditInput!) {
    editContractAsParty(id: $id, input: $input) { id title body terms dueDate status reviewNotes canEdit }
  }
`;
export const MY_CONTRACTS_QUERY = gql`
  query MyContracts {
    myContracts {
      id refNumber title body terms status penaltyClause cost costDescription dueDate createdAt
      templateId assignedToUser reviewNotes
      signedAt signatureData
      userSignatureData userSignedAt acceptedAt
      creator { id email }
      assignedUser { id email profile { firstName lastName } }
      parties { type name vendorName brideName groomName label }
      template { id name category }
      milestones { id stageName amount dueDate paidAt notes createdAt }
    }
  }
`;

// ── Admin Queries ──────────────────────────────────────────────

export const USERS_QUERY = gql`
  query Users {
    users {
      id
      email
      phone
      familyContacts { primary { name relation email phone } secondary { name relation email phone } }
      accountManager { userId name email phone roleName source endsOn assignedAt active }
      isVerified
      isActive
      menuVisibility
      role {
        id
        name
        displayName
      }
      subscription {
        id
        name
        displayName
        priceMonthly
      }
      permissions
      profile {
        id firstName lastName gender dob
        motherTongue religion community caste subCaste
        heightCm maritalStatus education profession employmentType annualIncome
        country state city pincode aboutMe familyBackground
        horoscopeStar horoscopeRasi horoscopeGothram birthTime birthPlace birthUtcOffset horoscopeUrl
        photoUrl profileCompletion
      }
    }
  }
`;

export const ROLES_QUERY = gql`
  query Roles {
    roles {
      id
      name
      displayName
      description
      defaultSubscription { id name displayName }
      permissions {
        id
        name
        displayName
      }
    }
  }
`;

export const SUBSCRIPTIONS_QUERY = gql`
  query Subscriptions {
    subscriptions {
      id
      name
      displayName
      description
      priceMonthly
      priceYearly
      permissions {
        id
        name
        displayName
      }
    }
  }
`;

export const PERMISSIONS_QUERY = gql`
  query Permissions {
    permissions {
      id
      name
      displayName
      description
    }
  }
`;

export const ASSIGN_ROLE_MUTATION = gql`
  mutation AssignRole($input: AssignRoleInput!) {
    assignRole(input: $input) {
      id
      email
      role {
        id
        name
        displayName
      }
    }
  }
`;

export const SET_MENU_VISIBILITY_MUTATION = gql`
  mutation SetMenuVisibility($userId: ID!, $menuVisibility: String!) {
    setMenuVisibility(userId: $userId, menuVisibility: $menuVisibility) {
      id
      email
      menuVisibility
    }
  }
`;

// Company settings: demo (seed) data visibility
const DEMO_DATA_FIELDS = 'hidden keptAdmin total active hiddenOfferings accounts { id email name role roleLabel isActive kept } groups { role label total active } modules { key label menus config canHide canDelete canRecreate }';
export const DEMO_DATA_STATUS_QUERY = gql`
  query DemoDataStatus { demoDataStatus { ${DEMO_DATA_FIELDS} } }
`;
export const RECREATE_DEMO_DATA_MUTATION = gql`
  mutation RecreateDemoData($scope: DemoDataScope) {
    recreateDemoData(scope: $scope) {
      before { accounts active contracts offerings guests wishlists bookings }
      after { accounts active contracts offerings guests wishlists bookings }
      status { ${DEMO_DATA_FIELDS} }
    }
  }
`;
export const DELETE_DEMO_DATA_MUTATION = gql`
  mutation DeleteDemoData($scope: DemoDataScope) {
    deleteDemoData(scope: $scope) {
      before { accounts active contracts offerings guests wishlists bookings }
      after { accounts active contracts offerings guests wishlists bookings }
      status { ${DEMO_DATA_FIELDS} }
    }
  }
`;
export const SET_DEMO_DATA_HIDDEN_MUTATION = gql`
  mutation SetDemoDataHidden($hidden: Boolean!, $scope: DemoDataScope) { setDemoDataHidden(hidden: $hidden, scope: $scope) { ${DEMO_DATA_FIELDS} } }
`;

// Admin > API Docs (generated from the schema; admins only)
export const API_DOCS_QUERY = gql`
  query ApiDocs { apiDocs }
`;

// Admin > Bulk Data Load
export const BULK_IMPORT_MODULES_QUERY = gql`
  query BulkImportModules { bulkImportModules }
`;
export const BULK_IMPORT_TEMPLATE_QUERY = gql`
  query BulkImportTemplate($module: String!) { bulkImportTemplate(module: $module) }
`;
export const BULK_EXPORT_CSV_QUERY = gql`
  query BulkExportCsv($module: String!) { bulkExportCsv(module: $module) }
`;
export const BULK_IMPORT_MUTATION = gql`
  mutation BulkImport($module: String!, $csv: String!, $mode: String!, $existing: String, $onlyValid: Boolean) {
    bulkImport(module: $module, csv: $csv, mode: $mode, existing: $existing, onlyValid: $onlyValid)
  }
`;

export const DEACTIVATE_USER_MUTATION = gql`
  mutation DeactivateUser($userId: ID!) {
    deactivateUser(userId: $userId) { id email isActive }
  }
`;

export const REACTIVATE_USER_MUTATION = gql`
  mutation ReactivateUser($userId: ID!) {
    reactivateUser(userId: $userId) { id email isActive }
  }
`;

export const ADMIN_RESET_PASSWORD_MUTATION = gql`
  mutation AdminResetPassword($userId: ID!, $newPassword: String!) {
    adminResetPassword(userId: $userId, newPassword: $newPassword)
  }
`;

export const ASSIGN_SUBSCRIPTION_MUTATION = gql`
  mutation AssignSubscription($input: AssignSubscriptionInput!) {
    assignSubscription(input: $input) {
      id
      email
      subscription {
        id
        name
        displayName
      }
    }
  }
`;

export const ADMIN_CREATE_USER_MUTATION = gql`
  mutation Signup($input: NewUserInput!) {
    signup(input: $input)
  }
`;

// ── Role Page Permissions ──────────────────────────────────────

export const ALL_ROLE_PAGE_PERMISSIONS_QUERY = gql`
  query AllRolePagePermissions {
    allRolePagePermissions {
      id roleId roleName page canCreate canRead canUpdate canDelete
    }
  }
`;

export const ROLE_PAGE_PERMISSIONS_QUERY = gql`
  query RolePagePermissions($roleId: ID!) {
    rolePagePermissions(roleId: $roleId) {
      id roleId roleName page canCreate canRead canUpdate canDelete
    }
  }
`;

export const BULK_SET_ROLE_PAGE_PERMISSIONS_MUTATION = gql`
  mutation BulkSetRolePagePermissions($roleId: ID!, $permissions: [PagePermissionInput!]!) {
    bulkSetRolePagePermissions(roleId: $roleId, permissions: $permissions) {
      id roleId roleName page canCreate canRead canUpdate canDelete
    }
  }
`;

// ── Permissions CRUD ──────────────────────────────────────────

export const CREATE_PERMISSION_MUTATION = gql`
  mutation CreatePermission($input: CreatePermissionInput!) {
    createPermission(input: $input) { id name displayName description }
  }
`;
export const UPDATE_PERMISSION_MUTATION = gql`
  mutation UpdatePermission($input: UpdatePermissionInput!) {
    updatePermission(input: $input) { id name displayName description }
  }
`;
export const DELETE_PERMISSION_MUTATION = gql`
  mutation DeletePermission($id: ID!) { deletePermission(id: $id) }
`;
export const ASSIGN_PERMISSION_TO_ROLE_MUTATION = gql`
  mutation AssignPermissionToRole($roleId: ID!, $permissionId: ID!) {
    assignPermissionToRole(roleId: $roleId, permissionId: $permissionId) {
      id name permissions { id name displayName }
    }
  }
`;
export const REMOVE_PERMISSION_FROM_ROLE_MUTATION = gql`
  mutation RemovePermissionFromRole($roleId: ID!, $permissionId: ID!) {
    removePermissionFromRole(roleId: $roleId, permissionId: $permissionId) {
      id name permissions { id name displayName }
    }
  }
`;
export const ASSIGN_PERMISSION_TO_SUBSCRIPTION_MUTATION = gql`
  mutation AssignPermissionToSubscription($subscriptionId: ID!, $permissionId: ID!) {
    assignPermissionToSubscription(subscriptionId: $subscriptionId, permissionId: $permissionId) {
      id name permissions { id name displayName }
    }
  }
`;
export const REMOVE_PERMISSION_FROM_SUBSCRIPTION_MUTATION = gql`
  mutation RemovePermissionFromSubscription($subscriptionId: ID!, $permissionId: ID!) {
    removePermissionFromSubscription(subscriptionId: $subscriptionId, permissionId: $permissionId) {
      id name permissions { id name displayName }
    }
  }
`;

// ── Role CRUD ─────────────────────────────────────────────────

export const CREATE_ROLE_MUTATION = gql`
  mutation CreateRole($input: CreateRoleInput!) {
    createRole(input: $input) { id name displayName description }
  }
`;
export const UPDATE_ROLE_MUTATION = gql`
  mutation UpdateRole($input: UpdateRoleInput!) {
    updateRole(input: $input) { id name displayName description permissions { id name displayName } }
  }
`;
export const DELETE_ROLE_MUTATION = gql`
  mutation DeleteRole($id: ID!) { deleteRole(id: $id) }
`;

// ── Subscription CRUD ─────────────────────────────────────────

export const CREATE_SUBSCRIPTION_MUTATION = gql`
  mutation CreateSubscription($input: CreateSubscriptionInput!) {
    createSubscription(input: $input) { id name displayName description priceMonthly priceYearly }
  }
`;
export const UPDATE_SUBSCRIPTION_MUTATION = gql`
  mutation UpdateSubscription($input: UpdateSubscriptionInput!) {
    updateSubscription(input: $input) { id name displayName description priceMonthly priceYearly permissions { id name displayName } }
  }
`;
export const DELETE_SUBSCRIPTION_MUTATION = gql`
  mutation DeleteSubscription($id: ID!) { deleteSubscription(id: $id) }
`;

// ── Contract CRUD ─────────────────────────────────────────────

export const CONTRACT_TEMPLATES_QUERY = gql`
  query ContractTemplates {
    contractTemplates { id name description body category isActive createdAt }
  }
`;
export const CONTRACT_QUERY = gql`
  query Contract($id: ID!) {
    contract(id: $id) {
      id title terms body status penaltyClause cost costDescription dueDate
      signedAt signatureData templateId assignedToUser createdAt updatedAt
      creator { id email }
      assignedUser { id email profile { firstName lastName } }
      parties { type name vendorName brideName groomName label }
      template { id name category }
    }
  }
`;
export const CREATE_CONTRACT_MUTATION = gql`
  mutation CreateContract($input: CreateContractInput!) {
    createContract(input: $input) {
      id title status cost dueDate assignedToUser createdAt
      creator { id email }
      assignedUser { id email }
      template { id name }
    }
  }
`;
export const UPDATE_CONTRACT_MUTATION = gql`
  mutation UpdateContract($input: UpdateContractInput!) {
    updateContract(input: $input) {
      id title status cost costDescription dueDate penaltyClause body terms
      assignedUser { id email }
    }
  }
`;
export const DELETE_CONTRACT_MUTATION = gql`
  mutation DeleteContract($id: ID!) { deleteContract(id: $id) }
`;
export const DUPLICATE_CONTRACT_MUTATION = gql`
  mutation DuplicateContract($id: ID!) {
    duplicateContract(id: $id) {
      id title status cost dueDate assignedToUser refNumber createdAt
      creator { id email }
      assignedUser { id email }
      template { id name }
    }
  }
`;
export const SIGN_CONTRACT_MUTATION = gql`
  mutation SignContract($id: ID!, $signatureData: String!) {
    signContract(id: $id, signatureData: $signatureData) {
      id status signedAt signatureData
    }
  }
`;

// ── Contract Workflow mutations ───────────────────────────────

export const SEND_CONTRACT_FOR_REVIEW_MUTATION = gql`
  mutation SendContractForReview($id: ID!) {
    sendContractForReview(id: $id) {
      id status assignedToUser assignedUser { id email }
    }
  }
`;

export const ADMIN_SIGN_CONTRACT_MUTATION = gql`
  mutation AdminSignContract($id: ID!, $signatureData: String!) {
    adminSignContract(id: $id, signatureData: $signatureData) {
      id status signedAt signatureData userSignatureData userSignedAt
    }
  }
`;

export const RESPOND_TO_CONTRACT_MUTATION = gql`
  mutation RespondToContract($id: ID!, $response: String!, $notes: String) {
    respondToContract(id: $id, response: $response, notes: $notes) {
      id status reviewNotes userSignatureData userSignedAt
    }
  }
`;

// The family/vendor accepts their fully signed, admin-approved contract (opens vendors / offerings)
export const ACCEPT_CONTRACT_MUTATION = gql`
  mutation AcceptContract($id: ID!) {
    acceptContract(id: $id) { id status acceptedAt }
  }
`;

export const USER_SIGN_CONTRACT_MUTATION = gql`
  mutation UserSignContract($id: ID!, $signatureData: String!) {
    userSignContract(id: $id, signatureData: $signatureData) {
      id status userSignatureData userSignedAt signatureData signedAt
    }
  }
`;

// ── Contract Template CRUD ────────────────────────────────────

export const CREATE_CONTRACT_TEMPLATE_MUTATION = gql`
  mutation CreateContractTemplate($input: CreateContractTemplateInput!) {
    createContractTemplate(input: $input) { id name description body category isActive }
  }
`;
export const UPDATE_CONTRACT_TEMPLATE_MUTATION = gql`
  mutation UpdateContractTemplate($input: UpdateContractTemplateInput!) {
    updateContractTemplate(input: $input) { id name description body category isActive }
  }
`;
export const DELETE_CONTRACT_TEMPLATE_MUTATION = gql`
  mutation DeleteContractTemplate($id: ID!) { deleteContractTemplate(id: $id) }
`;

// ── LOV Admin ─────────────────────────────────────────────────

export const ADMIN_LOV_CATEGORIES_QUERY = gql`
  query AdminLovCategories {
    adminLovCategories
  }
`;

export const ADMIN_LIST_OF_VALUES_QUERY = gql`
  query AdminListOfValues($category: String!) {
    adminListOfValues(category: $category) {
      id category value label sortOrder isActive
    }
  }
`;

export const CREATE_LOV_MUTATION = gql`
  mutation CreateLov($category: String!, $value: String!, $label: String!, $sortOrder: Int) {
    createLov(category: $category, value: $value, label: $label, sortOrder: $sortOrder) {
      id category value label sortOrder isActive
    }
  }
`;

export const UPDATE_LOV_MUTATION = gql`
  mutation UpdateLov($id: ID!, $label: String, $sortOrder: Int, $isActive: Boolean) {
    updateLov(id: $id, label: $label, sortOrder: $sortOrder, isActive: $isActive) {
      id category value label sortOrder isActive
    }
  }
`;

export const DELETE_LOV_MUTATION = gql`
  mutation DeleteLov($id: ID!) { deleteLov(id: $id) }
`;

// ── Wish Lists ────────────────────────────────────────────────

export const MY_WISH_LISTS_QUERY = gql`
  query MyWishLists {
    myWishLists { id name items isCompleted budget budgetNotes eventScale priority createdAt updatedAt }
  }
`;

export const CREATE_WISH_LIST_MUTATION = gql`
  mutation CreateWishList($name: String!, $budget: Float, $budgetNotes: String, $eventScale: String, $priority: String, $templateId: ID) {
    createWishList(name: $name, budget: $budget, budgetNotes: $budgetNotes, eventScale: $eventScale, priority: $priority, templateId: $templateId) {
      id name items isCompleted budget budgetNotes eventScale priority createdAt
    }
  }
`;

export const UPDATE_WISH_LIST_MUTATION = gql`
  mutation UpdateWishList($id: ID!, $name: String, $items: JSON, $isCompleted: Boolean, $budget: Float, $budgetNotes: String, $eventScale: String, $priority: String) {
    updateWishList(id: $id, name: $name, items: $items, isCompleted: $isCompleted, budget: $budget, budgetNotes: $budgetNotes, eventScale: $eventScale, priority: $priority) {
      id name items isCompleted budget budgetNotes eventScale priority updatedAt
    }
  }
`;

export const DELETE_WISH_LIST_MUTATION = gql`
  mutation DeleteWishList($id: ID!) { deleteWishList(id: $id) }
`;

export const DUPLICATE_WISH_LIST_MUTATION = gql`
  mutation DuplicateWishList($id: ID!) {
    duplicateWishList(id: $id) {
      id name items isCompleted budget budgetNotes eventScale priority createdAt
    }
  }
`;

export const WISH_LIST_TEMPLATES_QUERY = gql`
  query WishlistTemplates($tradition: String) {
    wishlistTemplates(tradition: $tradition) { id name tradition eventScale priority items isActive }
  }
`;

// ── Service Questionnaire & Wishlist (Tile Q&A) ────────────────

export const SERVICE_CATEGORY_TILES_QUERY = gql`
  query ServiceCategoryTiles {
    serviceCategoryTiles {
      category displayName description icon serviceCount
      services { id name category description unit basePrice taxRate isActive imageUrl tags }
    }
  }
`;

export const SERVICE_SHOWCASE_QUERY = gql`
  query ServiceShowcase($category: String!) {
    serviceShowcase(category: $category) {
      category packageLabel itemLabel providerCount packageCount priceFrom priceTo priceUnit
      highlights { label values }
      packages { name description price priceUnit itemCount photos { url caption } sections { name count items } }
      images { url caption }
      themes { name style description colorPalette estimatedCost imageUrl }
    }
  }
`;

export const QUESTIONNAIRE_QUESTIONS_QUERY = gql`
  query QuestionnaireQuestions($category: String!, $budget: Float, $guestCount: Int) {
    questionnaireQuestions(category: $category, budget: $budget, guestCount: $guestCount) {
      id questionKey question type options required category dependsOn dependsOnValue autoAnswer
    }
  }
`;

export const MY_SERVICE_QUESTIONNAIRES_QUERY = gql`
  query MyServiceQuestionnaires($forWhom: String) {
    myServiceQuestionnaires(forWhom: $forWhom) {
      id userId forWhom contractorId serviceCategory selectedServices responses status budget priority notes createdAt updatedAt
    }
  }
`;

export const MY_SERVICE_WISHLISTS_QUERY = gql`
  query MyServiceWishlists($forWhom: String, $status: String) {
    myServiceWishlists(forWhom: $forWhom, status: $status) {
      id userId groomUserId forWhom contractorId status serviceSelections totalBudget
      eventDate eventCity eventScale guestCount brideConfirmed groomConfirmed contractorConfirmed
      preferredVendors { category categoryName offeringId packageId packageName title city auto available }
      confirmedAt contractId notes createdAt updatedAt
      user { id email profile { firstName lastName } }
      groomUser { id email profile { firstName lastName } }
      contract {
        id title refNumber status cost costDescription dueDate createdAt
        body terms penaltyClause
        signatureData signedAt userSignatureData userSignedAt
        assignedUser { id email profile { firstName lastName } }
        milestones { id stageName amount dueDate paidAt notes createdAt }
      }
    }
  }
`;

export const SERVICE_WISHLIST_QUERY = gql`
  query ServiceWishlist($id: ID!) {
    serviceWishlist(id: $id) {
      id userId groomUserId forWhom contractorId status serviceSelections totalBudget
      eventDate eventCity eventScale guestCount brideConfirmed groomConfirmed contractorConfirmed
      confirmedAt contractId notes createdAt updatedAt
      user { id email role { name displayName } profile { firstName lastName } }
      groomUser { id email role { name displayName } profile { firstName lastName } }
      contractor { id email profile { firstName lastName } }
      contract { id title status refNumber }
    }
  }
`;

export const CLIENT_SERVICE_WISHLISTS_QUERY = gql`
  query ClientServiceWishlists($clientId: ID) {
    clientServiceWishlists(clientId: $clientId) {
      id userId groomUserId forWhom status serviceSelections totalBudget eventDate eventScale
      brideConfirmed groomConfirmed contractorConfirmed confirmedAt contractId notes createdAt
      user { id email profile { firstName lastName } }
      groomUser { id email profile { firstName lastName } }
    }
  }
`;

export const SAVE_QUESTIONNAIRE_MUTATION = gql`
  mutation SaveQuestionnaireResponse($input: SaveQuestionnaireInput!) {
    saveQuestionnaireResponse(input: $input) {
      id userId forWhom serviceCategory selectedServices responses status budget priority notes createdAt
    }
  }
`;

export const COMPLETE_QUESTIONNAIRE_MUTATION = gql`
  mutation CompleteQuestionnaire($id: ID!) {
    completeQuestionnaire(id: $id) { id status }
  }
`;

export const CREATE_SERVICE_WISHLIST_MUTATION = gql`
  mutation CreateServiceWishlist($input: CreateServiceWishlistInput!) {
    createServiceWishlist(input: $input) {
      id userId forWhom status serviceSelections totalBudget eventDate eventCity eventScale guestCount createdAt
    }
  }
`;

export const UPDATE_SERVICE_WISHLIST_MUTATION = gql`
  mutation UpdateServiceWishlist($input: UpdateServiceWishlistInput!) {
    updateServiceWishlist(input: $input) {
      id status forWhom serviceSelections totalBudget eventDate eventCity eventScale guestCount notes updatedAt
    }
  }
`;

export const CONFIRM_SERVICE_WISHLIST_MUTATION = gql`
  mutation ConfirmServiceWishlist($id: ID!, $role: String!) {
    confirmServiceWishlist(id: $id, role: $role) {
      id status brideConfirmed groomConfirmed contractorConfirmed confirmedAt
    }
  }
`;

export const SUBMIT_WISHLIST_FOR_APPROVAL_MUTATION = gql`
  mutation SubmitWishlistForApproval($id: ID!) {
    submitWishlistForApproval(id: $id) { id status }
  }
`;

export const APPROVE_SERVICE_WISHLIST_MUTATION = gql`
  mutation ApproveServiceWishlist($id: ID!) {
    approveServiceWishlist(id: $id) {
      id status approvedAt contractId
    }
  }
`;

export const REJECT_SERVICE_WISHLIST_MUTATION = gql`
  mutation RejectServiceWishlist($id: ID!, $reason: String) {
    rejectServiceWishlist(id: $id, reason: $reason) { id status rejectionReason }
  }
`;

export const DELETE_SERVICE_WISHLIST_MUTATION = gql`
  mutation DeleteServiceWishlist($id: ID!) {
    deleteServiceWishlist(id: $id)
  }
`;

export const ADMIN_CREATE_SERVICE_WISHLIST_MUTATION = gql`
  mutation AdminCreateServiceWishlist($input: CreateServiceWishlistInput!) {
    createServiceWishlist(input: $input) {
      id userId forWhom status serviceSelections totalBudget eventDate eventCity eventScale guestCount notes createdAt
    }
  }
`;

export const ALL_SERVICE_WISHLISTS_QUERY = gql`
  query AllServiceWishlists($status: String) {
    allServiceWishlists(status: $status) {
      id userId groomUserId forWhom contractorId status serviceSelections totalBudget
      eventDate eventCity eventScale guestCount brideConfirmed groomConfirmed contractorConfirmed
      preferredVendors { category categoryName offeringId packageId packageName title city auto businessName available }
      confirmedAt contractId rejectionReason approvedAt notes createdAt updatedAt
      user { id email profile { firstName lastName } }
      groomUser { id email profile { firstName lastName } }
      contractor { id email profile { firstName lastName } }
    }
  }
`;

export const CONVERT_WISHLIST_TO_CONTRACT_MUTATION = gql`
  mutation ConvertWishlistToContract($wishlistId: ID!) {
    convertWishlistToContract(wishlistId: $wishlistId) {
      id title status refNumber cost assignedToUser createdAt
    }
  }
`;

export const DUPLICATE_SERVICE_WISHLIST_MUTATION = gql`
  mutation DuplicateServiceWishlist($id: ID!, $forWhom: String, $clientId: ID, $groomClientId: ID) {
    duplicateServiceWishlist(id: $id, forWhom: $forWhom, clientId: $clientId, groomClientId: $groomClientId) {
      id userId groomUserId forWhom status serviceSelections totalBudget eventDate eventCity eventScale guestCount createdAt
    }
  }
`;

// ── Question Template CRUD (Admin - dynamic questions per service) ──────────

export const ALL_QUESTION_TEMPLATES_QUERY = gql`
  query AllQuestionTemplates($category: String) {
    allQuestionTemplates(category: $category) {
      id questionKey question type options required category sortOrder dependsOn dependsOnValue isActive createdAt updatedAt
      minBudget maxBudget optionBudgets { option minBudget } budgetAnswer
    }
  }
`;

export const QUESTION_CATEGORIES_QUERY = gql`
  query QuestionCategories { questionCategories }
`;

export const CREATE_QUESTION_TEMPLATE_MUTATION = gql`
  mutation CreateQuestionTemplate($input: CreateQuestionTemplateInput!) {
    createQuestionTemplate(input: $input) {
      id questionKey question type options required category sortOrder dependsOn dependsOnValue isActive
      minBudget maxBudget optionBudgets { option minBudget } budgetAnswer
    }
  }
`;

export const UPDATE_QUESTION_TEMPLATE_MUTATION = gql`
  mutation UpdateQuestionTemplate($input: UpdateQuestionTemplateInput!) {
    updateQuestionTemplate(input: $input) {
      id questionKey question type options required category sortOrder dependsOn dependsOnValue isActive
      minBudget maxBudget optionBudgets { option minBudget } budgetAnswer
    }
  }
`;

export const DELETE_QUESTION_TEMPLATE_MUTATION = gql`
  mutation DeleteQuestionTemplate($id: ID!) { deleteQuestionTemplate(id: $id) }
`;

export const REORDER_QUESTION_TEMPLATES_MUTATION = gql`
  mutation ReorderQuestionTemplates($category: String!, $orderedIds: [ID!]!) {
    reorderQuestionTemplates(category: $category, orderedIds: $orderedIds) {
      id sortOrder
    }
  }
`;

// ── Contract Milestones ────────────────────────────────────────

export const ADD_CONTRACT_MILESTONE_MUTATION = gql`
  mutation AddContractMilestone($contractId: ID!, $stageName: String!, $amount: Float!, $dueDate: String, $notes: String) {
    addContractMilestone(contractId: $contractId, stageName: $stageName, amount: $amount, dueDate: $dueDate, notes: $notes) {
      id contractId stageName amount dueDate paidAt notes createdAt
    }
  }
`;

export const UPDATE_CONTRACT_MILESTONE_MUTATION = gql`
  mutation UpdateContractMilestone($id: ID!, $stageName: String, $amount: Float, $dueDate: String, $notes: String) {
    updateContractMilestone(id: $id, stageName: $stageName, amount: $amount, dueDate: $dueDate, notes: $notes) {
      id contractId stageName amount dueDate paidAt notes
    }
  }
`;

export const MARK_MILESTONE_PAID_MUTATION = gql`
  mutation MarkMilestonePaid($id: ID!) {
    markMilestonePaid(id: $id) { id contractId stageName amount dueDate paidAt notes }
  }
`;

export const DELETE_CONTRACT_MILESTONE_MUTATION = gql`
  mutation DeleteContractMilestone($id: ID!) { deleteContractMilestone(id: $id) }
`;

// ── Messaging ─────────────────────────────────────────────────

export const MY_CONVERSATIONS_QUERY = gql`
  query MyConversations {
    myConversations {
      userId email firstName lastName unreadCount
      lastMessage { id body isRead createdAt fromUser toUser }
    }
  }
`;

export const MESSAGES_QUERY = gql`
  query Messages($withUserId: ID!) {
    messages(withUserId: $withUserId) {
      id fromUser toUser body isRead createdAt
      sender    { id email }
      recipient { id email }
    }
  }
`;

export const UNREAD_MESSAGE_COUNT_QUERY = gql`
  query UnreadMessageCount {
    unreadMessageCount
  }
`;

export const SEND_MESSAGE_MUTATION = gql`
  mutation SendMessage($toUserId: ID!, $body: String!) {
    sendMessage(toUserId: $toUserId, body: $body) {
      id fromUser toUser body isRead createdAt
      sender { id email }
      recipient { id email }
    }
  }
`;

export const MARK_MESSAGES_READ_MUTATION = gql`
  mutation MarkMessagesRead($fromUserId: ID!) {
    markMessagesRead(fromUserId: $fromUserId)
  }
`;

// ── Meetings ──────────────────────────────────────────────────

export const MY_MEETINGS_QUERY = gql`
  query MyMeetings {
    myMeetings {
      id organiserId title description proposedAt durationMinutes
      location meetingType status myResponse createdAt
      organiser { id email }
      participants {
        id userId response
        user { id email }
      }
    }
  }
`;

export const CREATE_MEETING_MUTATION = gql`
  mutation CreateMeeting($input: CreateMeetingInput!) {
    createMeeting(input: $input) {
      id title proposedAt status myResponse
      organiser { id email }
      participants { id userId response user { id email } }
    }
  }
`;

export const RESPOND_TO_MEETING_MUTATION = gql`
  mutation RespondToMeeting($meetingId: ID!, $response: String!) {
    respondToMeeting(meetingId: $meetingId, response: $response) {
      id status myResponse
      participants { id userId response user { id email } }
    }
  }
`;

export const CANCEL_MEETING_MUTATION = gql`
  mutation CancelMeeting($meetingId: ID!) {
    cancelMeeting(meetingId: $meetingId) { id status }
  }
`;

// ════════════════════════════════════════════════════════
// PHASE 2 — Ceremony / Ritual Planning
// ════════════════════════════════════════════════════════

export const CEREMONY_TEMPLATES_QUERY = gql`
  query CeremonyTemplates {
    ceremonyTemplates {
      id ceremonyType displayName description
      defaultTasks defaultPersonnel defaultMaterials sortOrder
    }
  }
`;

export const MY_CEREMONIES_QUERY = gql`
  query MyCeremonies {
    myCeremonies {
      id name ceremonyType eventDate venue notes status createdAt sourceOfferingId sourceVendor
      tasks {
        id taskName category assignedTo dueTime isDone notes sortOrder
      }
      personnel {
        id role name contact confirmed notes
      }
    }
  }
`;

export const CEREMONY_QUERY = gql`
  query Ceremony($id: ID!) {
    ceremony(id: $id) {
      id name ceremonyType eventDate venue notes status createdAt
      tasks {
        id taskName category assignedTo dueTime isDone notes sortOrder
      }
      personnel {
        id role name contact confirmed notes
      }
    }
  }
`;

// Plan a ceremony from a contracted rituals vendor's ceremony (its items become the checklist)
export const CREATE_CEREMONY_FROM_PACKAGE_MUTATION = gql`
  mutation CreateCeremonyFromPackage($offeringId: ID!, $packageId: ID!, $name: String, $eventDate: String, $venue: String, $notes: String) {
    createCeremonyFromPackage(offeringId: $offeringId, packageId: $packageId, name: $name, eventDate: $eventDate, venue: $venue, notes: $notes) {
      id name ceremonyType sourceVendor
    }
  }
`;

export const CREATE_CEREMONY_MUTATION = gql`
  mutation CreateCeremony($name: String!, $ceremonyType: String!, $eventDate: String, $venue: String, $notes: String) {
    createCeremony(name: $name, ceremonyType: $ceremonyType, eventDate: $eventDate, venue: $venue, notes: $notes) {
      id name ceremonyType eventDate venue status
      tasks { id taskName category isDone sortOrder }
      personnel { id role name confirmed }
    }
  }
`;

export const UPDATE_CEREMONY_MUTATION = gql`
  mutation UpdateCeremony($id: ID!, $name: String, $ceremonyType: String, $eventDate: String, $venue: String, $notes: String, $status: String) {
    updateCeremony(id: $id, name: $name, ceremonyType: $ceremonyType, eventDate: $eventDate, venue: $venue, notes: $notes, status: $status) {
      id name ceremonyType eventDate venue notes status
    }
  }
`;

export const DELETE_CEREMONY_MUTATION = gql`
  mutation DeleteCeremony($id: ID!) { deleteCeremony(id: $id) }
`;

export const ADD_RITUAL_TASK_MUTATION = gql`
  mutation AddRitualTask($ceremonyId: ID!, $taskName: String!, $category: String, $assignedTo: String, $dueTime: String, $notes: String, $sortOrder: Int) {
    addRitualTask(ceremonyId: $ceremonyId, taskName: $taskName, category: $category, assignedTo: $assignedTo, dueTime: $dueTime, notes: $notes, sortOrder: $sortOrder) {
      id taskName category assignedTo dueTime isDone notes sortOrder
    }
  }
`;

export const TOGGLE_RITUAL_TASK_MUTATION = gql`
  mutation ToggleRitualTask($id: ID!) {
    toggleRitualTask(id: $id) { id isDone }
  }
`;

export const DELETE_RITUAL_TASK_MUTATION = gql`
  mutation DeleteRitualTask($id: ID!) { deleteRitualTask(id: $id) }
`;

export const ADD_RITUAL_PERSONNEL_MUTATION = gql`
  mutation AddRitualPersonnel($ceremonyId: ID!, $role: String!, $name: String, $contact: String, $notes: String) {
    addRitualPersonnel(ceremonyId: $ceremonyId, role: $role, name: $name, contact: $contact, notes: $notes) {
      id role name contact confirmed notes
    }
  }
`;

export const UPDATE_RITUAL_PERSONNEL_MUTATION = gql`
  mutation UpdateRitualPersonnel($id: ID!, $name: String, $contact: String, $confirmed: Boolean, $notes: String) {
    updateRitualPersonnel(id: $id, name: $name, contact: $contact, confirmed: $confirmed, notes: $notes) {
      id role name contact confirmed notes
    }
  }
`;

export const DELETE_RITUAL_PERSONNEL_MUTATION = gql`
  mutation DeleteRitualPersonnel($id: ID!) { deleteRitualPersonnel(id: $id) }
`;

// ════════════════════════════════════════════════════════
// PHASE 2 — Venues
// ════════════════════════════════════════════════════════

export const VENUES_QUERY = gql`
  query Venues($city: String, $venueType: String, $minCapacity: Int, $maxCapacity: Int) {
    venues(city: $city, venueType: $venueType, minCapacity: $minCapacity, maxCapacity: $maxCapacity) {
      id name address city state venueType
      capacityMin capacityMax pricePerDay pricePerHalfDay
      facilities contactName contactPhone contactEmail website notes
      averageRating reviewCount
    }
  }
`;

export const MY_VENUE_BOOKINGS_QUERY = gql`
  query MyVenueBookings {
    myVenueBookings {
      id eventDate slot guestCount status totalCost advancePaid specialRequests notes createdAt
      venue { id name city venueType }
      ceremony { id name ceremonyType eventDate }
    }
  }
`;

export const BOOK_VENUE_MUTATION = gql`
  mutation BookVenue($venueId: ID!, $ceremonyId: ID, $eventDate: String!, $slot: String, $guestCount: Int, $specialRequests: String, $notes: String) {
    bookVenue(venueId: $venueId, ceremonyId: $ceremonyId, eventDate: $eventDate, slot: $slot, guestCount: $guestCount, specialRequests: $specialRequests, notes: $notes) {
      id eventDate slot status
      venue { id name city }
    }
  }
`;

export const UPDATE_VENUE_BOOKING_MUTATION = gql`
  mutation UpdateVenueBooking($id: ID!, $status: String, $guestCount: Int, $totalCost: Float, $advancePaid: Float, $notes: String) {
    updateVenueBooking(id: $id, status: $status, guestCount: $guestCount, totalCost: $totalCost, advancePaid: $advancePaid, notes: $notes) {
      id eventDate slot status totalCost advancePaid
    }
  }
`;

export const CANCEL_VENUE_BOOKING_MUTATION = gql`
  mutation CancelVenueBooking($id: ID!) {
    cancelVenueBooking(id: $id) { id status }
  }
`;

// ════════════════════════════════════════════════════════
// PHASE 2 — Vendors
// ════════════════════════════════════════════════════════

export const VENDORS_QUERY = gql`
  query Vendors($city: String, $vendorType: String) {
    vendors(city: $city, vendorType: $vendorType) {
      id name vendorType city state
      priceMin priceMax pricingModel description
      contactName contactPhone contactEmail website
      portfolioUrls averageRating reviewCount isActive
    }
  }
`;

export const VENDOR_REVIEWS_QUERY = gql`
  query VendorReviews($vendorId: ID!) {
    vendorReviews(vendorId: $vendorId) {
      id rating reviewText createdAt
      reviewer { id email }
    }
  }
`;

export const MY_VENDOR_BOOKINGS_QUERY = gql`
  query MyVendorBookings {
    myVendorBookings {
      id eventDate agreedPrice advancePaid status notes createdAt
      vendor { id name vendorType city }
      ceremony { id name ceremonyType eventDate }
    }
  }
`;

export const BOOK_VENDOR_MUTATION = gql`
  mutation BookVendor($vendorId: ID!, $ceremonyId: ID, $eventDate: String!, $agreedPrice: Float, $notes: String) {
    bookVendor(vendorId: $vendorId, ceremonyId: $ceremonyId, eventDate: $eventDate, agreedPrice: $agreedPrice, notes: $notes) {
      id eventDate status agreedPrice
      vendor { id name vendorType }
    }
  }
`;

export const ADD_VENDOR_REVIEW_MUTATION = gql`
  mutation AddVendorReview($vendorId: ID!, $rating: Int!, $reviewText: String) {
    addVendorReview(vendorId: $vendorId, rating: $rating, reviewText: $reviewText) {
      id rating reviewText createdAt reviewer { id email }
    }
  }
`;

export const CANCEL_VENDOR_BOOKING_MUTATION = gql`
  mutation CancelVendorBooking($id: ID!) {
    cancelVendorBooking(id: $id) { id status }
  }
`;

// ── Availability ──────────────────────────────────────────────

export const VENDOR_AVAILABILITY_QUERY = gql`
  query VendorAvailability($vendorId: ID!, $fromDate: String, $toDate: String) {
    vendorAvailability(vendorId: $vendorId, fromDate: $fromDate, toDate: $toDate) {
      id eventDate isAvailable notes
    }
  }
`;

export const VENUE_AVAILABILITY_QUERY = gql`
  query VenueAvailability($venueId: ID!, $fromDate: String, $toDate: String) {
    venueAvailability(venueId: $venueId, fromDate: $fromDate, toDate: $toDate) {
      id eventDate slot status notes
    }
  }
`;

export const VENUE_REVIEWS_QUERY = gql`
  query VenueReviews($venueId: ID!) {
    venueReviews(venueId: $venueId) {
      id venueId userId rating reviewText createdAt
      reviewer { id email }
    }
  }
`;

export const ADD_VENUE_REVIEW_MUTATION = gql`
  mutation AddVenueReview($venueId: ID!, $rating: Int!, $reviewText: String) {
    addVenueReview(venueId: $venueId, rating: $rating, reviewText: $reviewText) {
      id rating reviewText createdAt reviewer { id email }
    }
  }
`;

export const CEREMONY_MATERIALS_QUERY = gql`
  query CeremonyMaterials($ceremonyId: ID!) {
    ceremonyMaterials(ceremonyId: $ceremonyId) {
      id ceremonyId itemName category quantity unit quantityNum
      estimatedCost actualCost vendorName status notes sortOrder
    }
  }
`;

export const ADD_CEREMONY_MATERIAL_MUTATION = gql`
  mutation AddCeremonyMaterial($ceremonyId: ID!, $itemName: String!, $category: String, $quantity: String, $unit: String, $quantityNum: Float, $estimatedCost: Float, $vendorName: String, $notes: String, $sortOrder: Int) {
    addCeremonyMaterial(ceremonyId: $ceremonyId, itemName: $itemName, category: $category, quantity: $quantity, unit: $unit, quantityNum: $quantityNum, estimatedCost: $estimatedCost, vendorName: $vendorName, notes: $notes, sortOrder: $sortOrder) {
      id itemName category quantity unit estimatedCost vendorName status notes
    }
  }
`;

export const UPDATE_CEREMONY_MATERIAL_MUTATION = gql`
  mutation UpdateCeremonyMaterial($id: ID!, $itemName: String, $category: String, $quantity: String, $unit: String, $quantityNum: Float, $estimatedCost: Float, $actualCost: Float, $vendorName: String, $status: String, $notes: String) {
    updateCeremonyMaterial(id: $id, itemName: $itemName, category: $category, quantity: $quantity, unit: $unit, quantityNum: $quantityNum, estimatedCost: $estimatedCost, actualCost: $actualCost, vendorName: $vendorName, status: $status, notes: $notes) {
      id itemName category quantity unit estimatedCost actualCost vendorName status notes
    }
  }
`;

export const DELETE_CEREMONY_MATERIAL_MUTATION = gql`
  mutation DeleteCeremonyMaterial($id: ID!) {
    deleteCeremonyMaterial(id: $id)
  }
`;

export const SYNC_CEREMONY_MATERIALS_MUTATION = gql`
  mutation SyncCeremonyMaterials($ceremonyId: ID!) {
    syncCeremonyMaterials(ceremonyId: $ceremonyId) {
      id itemName category quantity unit status notes sortOrder
    }
  }
`;

export const UPDATE_CEREMONY_TEMPLATE_MUTATION = gql`
  mutation UpdateCeremonyTemplate(
    $id: ID!
    $displayName: String
    $description: String
    $defaultMaterials: JSON
    $defaultTasks: JSON
    $defaultPersonnel: JSON
    $sortOrder: Int
    $isActive: Boolean
  ) {
    updateCeremonyTemplate(
      id: $id
      displayName: $displayName
      description: $description
      defaultMaterials: $defaultMaterials
      defaultTasks: $defaultTasks
      defaultPersonnel: $defaultPersonnel
      sortOrder: $sortOrder
      isActive: $isActive
    ) {
      id ceremonyType displayName description defaultMaterials defaultTasks defaultPersonnel sortOrder isActive
    }
  }
`;

export const CREATE_CEREMONY_TEMPLATE_MUTATION = gql`
  mutation CreateCeremonyTemplate(
    $ceremonyType: String!
    $displayName: String!
    $description: String
    $defaultMaterials: JSON
    $defaultTasks: JSON
    $defaultPersonnel: JSON
    $sortOrder: Int
  ) {
    createCeremonyTemplate(
      ceremonyType: $ceremonyType
      displayName: $displayName
      description: $description
      defaultMaterials: $defaultMaterials
      defaultTasks: $defaultTasks
      defaultPersonnel: $defaultPersonnel
      sortOrder: $sortOrder
    ) {
      id ceremonyType displayName description defaultMaterials defaultTasks defaultPersonnel sortOrder isActive
    }
  }
`;

// ─── Phase 3: Catering ────────────────────────────────────────────────────────

export const CATERERS_QUERY = gql`
  query Caterers($cuisineType: String, $city: String) {
    caterers(cuisineType: $cuisineType, city: $city) {
      id name contactPerson phone email cuisineTypes
      minGuests maxGuests pricePerPersonVeg pricePerPersonNonveg
      city description rating isActive
      menuItems { id itemName course cuisineType isVeg pricePerPerson sortOrder }
    }
  }
`;

export const CATERER_QUERY = gql`
  query Caterer($id: ID!) {
    caterer(id: $id) {
      id name contactPerson phone email cuisineTypes
      minGuests maxGuests pricePerPersonVeg pricePerPersonNonveg
      city description rating isActive
      menuItems { id itemName course cuisineType isVeg pricePerPerson sortOrder }
    }
  }
`;

export const MENU_ITEMS_QUERY = gql`
  query MenuItems($catererId: ID!) {
    menuItems(catererId: $catererId) {
      id itemName course cuisineType isVeg pricePerPerson sortOrder
    }
  }
`;

export const MY_CATERING_ORDERS_QUERY = gql`
  query MyCateringOrders($ceremonyId: ID!) {
    myCateringOrders(ceremonyId: $ceremonyId) {
      id status guestCount estimatedCost actualCost notes
      caterer { id name city cuisineTypes }
      items { id menuItem { id itemName course } quantity }
    }
  }
`;

export const CATERING_ORDER_QUERY = gql`
  query CateringOrder($id: ID!) {
    cateringOrder(id: $id) {
      id status guestCount estimatedCost actualCost notes
      caterer { id name city cuisineTypes }
      items { id menuItem { id itemName course } quantity }
    }
  }
`;

export const CREATE_CATERER_MUTATION = gql`
  mutation CreateCaterer(
    $name: String! $contactPerson: String $phone: String $email: String
    $cuisineTypes: [String] $minGuests: Int $maxGuests: Int
    $pricePerPersonVeg: Float $pricePerPersonNonveg: Float
    $city: String $description: String
  ) {
    createCaterer(
      name: $name contactPerson: $contactPerson phone: $phone email: $email
      cuisineTypes: $cuisineTypes minGuests: $minGuests maxGuests: $maxGuests
      pricePerPersonVeg: $pricePerPersonVeg pricePerPersonNonveg: $pricePerPersonNonveg
      city: $city description: $description
    ) { id name cuisineTypes city isActive }
  }
`;

export const UPDATE_CATERER_MUTATION = gql`
  mutation UpdateCaterer(
    $id: ID! $name: String $contactPerson: String $phone: String $email: String
    $cuisineTypes: [String] $minGuests: Int $maxGuests: Int
    $pricePerPersonVeg: Float $pricePerPersonNonveg: Float
    $city: String $description: String $isActive: Boolean
  ) {
    updateCaterer(
      id: $id name: $name contactPerson: $contactPerson phone: $phone email: $email
      cuisineTypes: $cuisineTypes minGuests: $minGuests maxGuests: $maxGuests
      pricePerPersonVeg: $pricePerPersonVeg pricePerPersonNonveg: $pricePerPersonNonveg
      city: $city description: $description isActive: $isActive
    ) { id name cuisineTypes city isActive }
  }
`;

export const DELETE_CATERER_MUTATION = gql`
  mutation DeleteCaterer($id: ID!) { deleteCaterer(id: $id) }
`;

export const ADD_MENU_ITEM_MUTATION = gql`
  mutation AddMenuItem(
    $catererId: ID! $itemName: String! $course: String $cuisineType: String
    $isVeg: Boolean $pricePerPerson: Float $sortOrder: Int
  ) {
    addMenuItem(
      catererId: $catererId itemName: $itemName course: $course cuisineType: $cuisineType
      isVeg: $isVeg pricePerPerson: $pricePerPerson sortOrder: $sortOrder
    ) { id itemName course cuisineType isVeg pricePerPerson sortOrder }
  }
`;

export const UPDATE_MENU_ITEM_MUTATION = gql`
  mutation UpdateMenuItem(
    $id: ID! $itemName: String $course: String $cuisineType: String
    $isVeg: Boolean $pricePerPerson: Float $sortOrder: Int
  ) {
    updateMenuItem(
      id: $id itemName: $itemName course: $course cuisineType: $cuisineType
      isVeg: $isVeg pricePerPerson: $pricePerPerson sortOrder: $sortOrder
    ) { id itemName course cuisineType isVeg pricePerPerson sortOrder }
  }
`;

export const DELETE_MENU_ITEM_MUTATION = gql`
  mutation DeleteMenuItem($id: ID!) { deleteMenuItem(id: $id) }
`;

export const CREATE_CATERING_ORDER_MUTATION = gql`
  mutation CreateCateringOrder(
    $ceremonyId: ID! $catererId: ID! $guestCount: Int $notes: String
  ) {
    createCateringOrder(
      ceremonyId: $ceremonyId catererId: $catererId
      guestCount: $guestCount notes: $notes
    ) { id status guestCount estimatedCost notes caterer { id name } }
  }
`;

export const UPDATE_CATERING_ORDER_MUTATION = gql`
  mutation UpdateCateringOrder(
    $id: ID! $status: String $guestCount: Int $notes: String
  ) {
    updateCateringOrder(
      id: $id status: $status guestCount: $guestCount notes: $notes
    ) { id status guestCount estimatedCost actualCost notes }
  }
`;

export const DELETE_CATERING_ORDER_MUTATION = gql`
  mutation DeleteCateringOrder($id: ID!) { deleteCateringOrder(id: $id) }
`;

export const ADD_CATERING_ORDER_ITEM_MUTATION = gql`
  mutation AddCateringOrderItem($orderId: ID! $menuItemId: ID! $quantity: Int) {
    addCateringOrderItem(orderId: $orderId menuItemId: $menuItemId quantity: $quantity) {
      id quantity menuItem { id itemName course }
    }
  }
`;

export const REMOVE_CATERING_ORDER_ITEM_MUTATION = gql`
  mutation RemoveCateringOrderItem($id: ID!) { removeCateringOrderItem(id: $id) }
`;

// ─── Phase 3: Guests ─────────────────────────────────────────────────────────

export const MY_GUESTS_QUERY = gql`
  query MyGuests {
    myGuests {
      id name phone email relation side rsvpStatus isVip
      dietaryRequirement notes
      attendance { id ceremonyId isAttending }
    }
  }
`;

export const CREATE_GUEST_MUTATION = gql`
  mutation CreateGuest(
    $name: String! $phone: String
    $relation: String $side: String $rsvpStatus: String
    $isVip: Boolean $dietaryRequirement: String $notes: String
  ) {
    createGuest(
      name: $name phone: $phone
      relation: $relation side: $side rsvpStatus: $rsvpStatus
      isVip: $isVip dietaryRequirement: $dietaryRequirement notes: $notes
    ) { id name phone rsvpStatus isVip side }
  }
`;

export const UPDATE_GUEST_MUTATION = gql`
  mutation UpdateGuest(
    $id: ID! $name: String $phone: String
    $relation: String $side: String $rsvpStatus: String
    $isVip: Boolean $dietaryRequirement: String $notes: String
  ) {
    updateGuest(
      id: $id name: $name phone: $phone
      relation: $relation side: $side rsvpStatus: $rsvpStatus
      isVip: $isVip dietaryRequirement: $dietaryRequirement notes: $notes
    ) { id name phone rsvpStatus isVip side }
  }
`;

export const DELETE_GUEST_MUTATION = gql`
  mutation DeleteGuest($id: ID!) { deleteGuest(id: $id) }
`;

export const SET_GUEST_ATTENDANCE_MUTATION = gql`
  mutation SetGuestAttendance(
    $guestId: ID! $ceremonyId: ID! $isAttending: Boolean!
  ) {
    setGuestAttendance(
      guestId: $guestId ceremonyId: $ceremonyId
      isAttending: $isAttending
    ) { id guestId ceremonyId isAttending }
  }
`;

// ─── Phase 3: Transportation ─────────────────────────────────────────────────

export const MY_TRANSPORT_REQUESTS_QUERY = gql`
  query MyTransportRequests($ceremonyId: ID) {
    myTransportRequests(ceremonyId: $ceremonyId) {
      id vehicleType status pickupLocation dropLocation
      pickupTime passengerCount notes
      guest { id name }
    }
  }
`;

export const CREATE_TRANSPORT_REQUEST_MUTATION = gql`
  mutation CreateTransportRequest(
    $ceremonyId: ID! $guestId: ID $vehicleType: String $pickupLocation: String
    $dropLocation: String $pickupTime: String $passengerCount: Int $notes: String
  ) {
    createTransportRequest(
      ceremonyId: $ceremonyId guestId: $guestId vehicleType: $vehicleType
      pickupLocation: $pickupLocation dropLocation: $dropLocation
      pickupTime: $pickupTime passengerCount: $passengerCount notes: $notes
    ) { id vehicleType status pickupLocation dropLocation passengerCount }
  }
`;

export const UPDATE_TRANSPORT_REQUEST_MUTATION = gql`
  mutation UpdateTransportRequest(
    $id: ID! $vehicleType: String $status: String $pickupLocation: String
    $dropLocation: String $pickupTime: String $passengerCount: Int $notes: String
  ) {
    updateTransportRequest(
      id: $id vehicleType: $vehicleType status: $status
      pickupLocation: $pickupLocation dropLocation: $dropLocation
      pickupTime: $pickupTime passengerCount: $passengerCount notes: $notes
    ) { id vehicleType status pickupLocation dropLocation passengerCount }
  }
`;

export const DELETE_TRANSPORT_REQUEST_MUTATION = gql`
  mutation DeleteTransportRequest($id: ID!) { deleteTransportRequest(id: $id) }
`;

// ─── Phase 3: Entertainment ──────────────────────────────────────────────────

export const ENTERTAINMENT_ACTS_QUERY = gql`
  query EntertainmentActs($actType: String, $city: String) {
    entertainmentActs(actType: $actType, city: $city) {
      id name actType contactPerson phone email description
      minDurationHours pricePerHour flatRate city rating isActive
    }
  }
`;

export const MY_EVENT_PROGRAMS_QUERY = gql`
  query MyEventPrograms($ceremonyId: ID!) {
    myEventPrograms(ceremonyId: $ceremonyId) {
      id programName startTime durationMinutes status notes sortOrder
      act { id name actType city pricePerHour flatRate }
    }
  }
`;

export const CREATE_EVENT_PROGRAM_MUTATION = gql`
  mutation CreateEventProgram(
    $ceremonyId: ID! $actId: ID $programName: String! $customActName: String
    $startTime: String $durationMinutes: Int $notes: String $sortOrder: Int
  ) {
    createEventProgram(
      ceremonyId: $ceremonyId actId: $actId programName: $programName customActName: $customActName
      startTime: $startTime durationMinutes: $durationMinutes notes: $notes sortOrder: $sortOrder
    ) { id programName durationMinutes status act { id name actType } }
  }
`;

export const UPDATE_EVENT_PROGRAM_MUTATION = gql`
  mutation UpdateEventProgram(
    $id: ID! $actId: ID $programName: String $customActName: String
    $startTime: String $durationMinutes: Int $status: String $notes: String $sortOrder: Int
  ) {
    updateEventProgram(
      id: $id actId: $actId programName: $programName customActName: $customActName
      startTime: $startTime durationMinutes: $durationMinutes status: $status notes: $notes sortOrder: $sortOrder
    ) { id programName durationMinutes status }
  }
`;

export const DELETE_EVENT_PROGRAM_MUTATION = gql`
  mutation DeleteEventProgram($id: ID!) { deleteEventProgram(id: $id) }
`;

export const CREATE_ENTERTAINMENT_ACT_MUTATION = gql`
  mutation CreateEntertainmentAct(
    $name: String! $actType: String! $contactPerson: String $phone: String
    $email: String $description: String $minDurationHours: Float
    $pricePerHour: Float $flatRate: Float $city: String
  ) {
    createEntertainmentAct(
      name: $name actType: $actType contactPerson: $contactPerson phone: $phone
      email: $email description: $description minDurationHours: $minDurationHours
      pricePerHour: $pricePerHour flatRate: $flatRate city: $city
    ) { id name actType city isActive }
  }
`;

export const UPDATE_ENTERTAINMENT_ACT_MUTATION = gql`
  mutation UpdateEntertainmentAct(
    $id: ID! $name: String $actType: String $contactPerson: String $phone: String
    $email: String $description: String $minDurationHours: Float
    $pricePerHour: Float $flatRate: Float $city: String $isActive: Boolean
  ) {
    updateEntertainmentAct(
      id: $id name: $name actType: $actType contactPerson: $contactPerson phone: $phone
      email: $email description: $description minDurationHours: $minDurationHours
      pricePerHour: $pricePerHour flatRate: $flatRate city: $city isActive: $isActive
    ) { id name actType city isActive }
  }
`;

export const DELETE_ENTERTAINMENT_ACT_MUTATION = gql`
  mutation DeleteEntertainmentAct($id: ID!) { deleteEntertainmentAct(id: $id) }
`;

// ─── Phase 3: Staffing ───────────────────────────────────────────────────────

export const MY_STAFFING_REQUIREMENTS_QUERY = gql`
  query MyStaffingRequirements($ceremonyId: ID!) {
    myStaffingRequirements(ceremonyId: $ceremonyId) {
      id roleName requiredCount confirmedCount status notes
    }
  }
`;

export const CREATE_STAFFING_REQUIREMENT_MUTATION = gql`
  mutation CreateStaffingRequirement(
    $ceremonyId: ID! $roleName: String! $requiredCount: Int $notes: String
  ) {
    createStaffingRequirement(
      ceremonyId: $ceremonyId roleName: $roleName requiredCount: $requiredCount notes: $notes
    ) { id roleName requiredCount confirmedCount status }
  }
`;

export const UPDATE_STAFFING_REQUIREMENT_MUTATION = gql`
  mutation UpdateStaffingRequirement(
    $id: ID! $roleName: String $requiredCount: Int $confirmedCount: Int
    $status: String $notes: String
  ) {
    updateStaffingRequirement(
      id: $id roleName: $roleName requiredCount: $requiredCount confirmedCount: $confirmedCount
      status: $status notes: $notes
    ) { id roleName requiredCount confirmedCount status }
  }
`;

export const DELETE_STAFFING_REQUIREMENT_MUTATION = gql`
  mutation DeleteStaffingRequirement($id: ID!) { deleteStaffingRequirement(id: $id) }
`;



// ════════════════════════════════════════════════════════════
// GAP MODULES — Parking, Decoration, Seating, VIP Escort, Compare
// ════════════════════════════════════════════════════════════

// ── Parking ──────────────────────────────────────────────────
export const MY_PARKING_SLOTS_QUERY = gql`
  query MyParkingSlots($ceremonyId: ID, $venueId: ID) {
    myParkingSlots(ceremonyId: $ceremonyId, venueId: $venueId) {
      id userId ceremonyId venueId zoneName slotNumber slotType status notes
      ceremony { id name }
      venue { id name }
      allocations { id guestId guestName vehicleNumber vehicleType isValet releasedAt notes
        guest { id name isVip }
      }
    }
  }
`;

export const CEREMONY_PARKING_STATS_QUERY = gql`
  query CeremonyParkingStats($ceremonyId: ID!) {
    ceremonyParkingStats(ceremonyId: $ceremonyId) { total available reserved occupied valetCount }
  }
`;

export const CREATE_PARKING_SLOT_MUTATION = gql`
  mutation CreateParkingSlot($ceremonyId: ID, $venueId: ID, $zoneName: String, $slotNumber: String!, $slotType: String, $notes: String) {
    createParkingSlot(ceremonyId: $ceremonyId, venueId: $venueId, zoneName: $zoneName, slotNumber: $slotNumber, slotType: $slotType, notes: $notes) { id slotNumber status }
  }
`;

export const UPDATE_PARKING_SLOT_MUTATION = gql`
  mutation UpdateParkingSlot($id: ID!, $zoneName: String, $slotNumber: String, $slotType: String, $status: String, $notes: String) {
    updateParkingSlot(id: $id, zoneName: $zoneName, slotNumber: $slotNumber, slotType: $slotType, status: $status, notes: $notes) { id slotNumber status }
  }
`;

export const DELETE_PARKING_SLOT_MUTATION = gql`
  mutation DeleteParkingSlot($id: ID!) { deleteParkingSlot(id: $id) }
`;

export const ALLOCATE_PARKING_MUTATION = gql`
  mutation AllocateParking($slotId: ID!, $guestId: ID, $guestName: String, $vehicleNumber: String, $vehicleType: String, $isValet: Boolean, $notes: String) {
    allocateParking(slotId: $slotId, guestId: $guestId, guestName: $guestName, vehicleNumber: $vehicleNumber, vehicleType: $vehicleType, isValet: $isValet, notes: $notes) {
      id guestName vehicleNumber isValet releasedAt
    }
  }
`;

export const RELEASE_PARKING_MUTATION = gql`
  mutation ReleaseParking($id: ID!) {
    releaseParking(id: $id) { id releasedAt }
  }
`;

// ── Decoration ────────────────────────────────────────────────
export const DECORATION_THEMES_QUERY = gql`
  query DecorationThemes($style: String) {
    decorationThemes(style: $style) { id name style colorPalette description estimatedCost imageUrl isActive }
  }
`;

export const MY_DECORATION_ASSIGNMENTS_QUERY = gql`
  query MyDecorationAssignments($ceremonyId: ID) {
    myDecorationAssignments(ceremonyId: $ceremonyId) {
      id ceremonyId themeId vendorId budget actualCost setupTime status notes
      ceremony { id name }
      theme { id name style colorPalette estimatedCost }
      vendor { id name }
    }
  }
`;

export const ASSIGN_DECORATION_MUTATION = gql`
  mutation AssignDecoration($ceremonyId: ID!, $themeId: ID, $customTheme: String, $vendorId: ID, $budget: Float, $setupTime: String, $notes: String) {
    assignDecoration(ceremonyId: $ceremonyId, themeId: $themeId, customTheme: $customTheme, vendorId: $vendorId, budget: $budget, setupTime: $setupTime, notes: $notes) { id status }
  }
`;

export const UPDATE_DECORATION_ASSIGNMENT_MUTATION = gql`
  mutation UpdateDecorationAssignment($id: ID!, $themeId: ID, $customTheme: String, $vendorId: ID, $budget: Float, $actualCost: Float, $setupTime: String, $status: String, $notes: String) {
    updateDecorationAssignment(id: $id, themeId: $themeId, customTheme: $customTheme, vendorId: $vendorId, budget: $budget, actualCost: $actualCost, setupTime: $setupTime, status: $status, notes: $notes) { id status }
  }
`;

export const DELETE_DECORATION_ASSIGNMENT_MUTATION = gql`
  mutation DeleteDecorationAssignment($id: ID!) { deleteDecorationAssignment(id: $id) }
`;

// ── Seating ────────────────────────────────────────────────────
export const MY_SEATING_ARRANGEMENTS_QUERY = gql`
  query MySeatingArrangements($ceremonyId: ID) {
    mySeatingArrangements(ceremonyId: $ceremonyId) {
      id ceremonyId name layoutType totalTables seatsPerTable
      seats { id arrangementId guestId tableNumber seatNumber seatLabel notes
        guest { id name isVip dietaryRequirement }
      }
    }
  }
`;

export const CREATE_SEATING_ARRANGEMENT_MUTATION = gql`
  mutation CreateSeatingArrangement($ceremonyId: ID!, $name: String!, $layoutType: String, $totalTables: Int, $seatsPerTable: Int, $notes: String) {
    createSeatingArrangement(ceremonyId: $ceremonyId, name: $name, layoutType: $layoutType, totalTables: $totalTables, seatsPerTable: $seatsPerTable, notes: $notes) { id name }
  }
`;

export const UPDATE_SEATING_ARRANGEMENT_MUTATION = gql`
  mutation UpdateSeatingArrangement($id: ID!, $name: String, $layoutType: String, $totalTables: Int, $seatsPerTable: Int, $notes: String) {
    updateSeatingArrangement(id: $id, name: $name, layoutType: $layoutType, totalTables: $totalTables, seatsPerTable: $seatsPerTable, notes: $notes) { id name }
  }
`;

export const DELETE_SEATING_ARRANGEMENT_MUTATION = gql`
  mutation DeleteSeatingArrangement($id: ID!) { deleteSeatingArrangement(id: $id) }
`;

export const ASSIGN_SEAT_MUTATION = gql`
  mutation AssignSeat($arrangementId: ID!, $guestId: ID, $tableNumber: Int!, $seatNumber: Int, $seatLabel: String, $notes: String) {
    assignSeat(arrangementId: $arrangementId, guestId: $guestId, tableNumber: $tableNumber, seatNumber: $seatNumber, seatLabel: $seatLabel, notes: $notes) { id tableNumber seatNumber }
  }
`;

export const UPDATE_SEAT_ASSIGNMENT_MUTATION = gql`
  mutation UpdateSeatAssignment($id: ID!, $guestId: ID, $tableNumber: Int, $seatNumber: Int, $seatLabel: String, $notes: String) {
    updateSeatAssignment(id: $id, guestId: $guestId, tableNumber: $tableNumber, seatNumber: $seatNumber, seatLabel: $seatLabel, notes: $notes) { id tableNumber }
  }
`;

export const REMOVE_SEAT_ASSIGNMENT_MUTATION = gql`
  mutation RemoveSeatAssignment($id: ID!) { removeSeatAssignment(id: $id) }
`;

// ── VIP Escorts ────────────────────────────────────────────────
export const MY_VIP_ESCORTS_QUERY = gql`
  query MyVipEscorts($ceremonyId: ID) {
    myVipEscorts(ceremonyId: $ceremonyId) {
      id guestId ceremonyId escortName escortRole fromLocation toLocation escortTime status notes
      guest { id name isVip }
      ceremony { id name }
    }
  }
`;

export const CREATE_VIP_ESCORT_MUTATION = gql`
  mutation CreateVipEscort($guestId: ID!, $ceremonyId: ID!, $escortName: String, $escortRole: String, $fromLocation: String, $toLocation: String, $escortTime: String, $notes: String) {
    createVipEscort(guestId: $guestId, ceremonyId: $ceremonyId, escortName: $escortName, escortRole: $escortRole, fromLocation: $fromLocation, toLocation: $toLocation, escortTime: $escortTime, notes: $notes) { id status }
  }
`;

export const UPDATE_VIP_ESCORT_MUTATION = gql`
  mutation UpdateVipEscort($id: ID!, $escortName: String, $escortRole: String, $fromLocation: String, $toLocation: String, $escortTime: String, $status: String, $notes: String) {
    updateVipEscort(id: $id, escortName: $escortName, escortRole: $escortRole, fromLocation: $fromLocation, toLocation: $toLocation, escortTime: $escortTime, status: $status, notes: $notes) { id status }
  }
`;

export const DELETE_VIP_ESCORT_MUTATION = gql`
  mutation DeleteVipEscort($id: ID!) { deleteVipEscort(id: $id) }
`;

// ── Caterer Comparison ─────────────────────────────────────────
export const COMPARE_CATERERS_QUERY = gql`
  query CompareCaterers($cuisineType: String, $city: String) {
    compareCaterers(cuisineType: $cuisineType, city: $city) {
      id name cuisineTypes city pricePerPersonVeg pricePerPersonNonveg rating minGuests maxGuests menuItemCount description
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Transport Vehicles & Assignments (Module 8 Gap)
// ─────────────────────────────────────────────────────────────────────────────

export const MY_TRANSPORT_VEHICLES_QUERY = gql`
  query MyTransportVehicles {
    myTransportVehicles {
      id vehicleNumber vehicleType capacity driverName driverPhone status notes
      vendor { id name }
      assignments {
        id assignedAt completedAt notes
        request { id guestName pickupLocation dropLocation pickupTime status }
      }
    }
  }
`;

export const CREATE_TRANSPORT_VEHICLE_MUTATION = gql`
  mutation CreateTransportVehicle($vehicleNumber: String!, $vehicleType: String, $capacity: Int, $driverName: String, $driverPhone: String, $vendorId: ID, $notes: String) {
    createTransportVehicle(vehicleNumber: $vehicleNumber, vehicleType: $vehicleType, capacity: $capacity, driverName: $driverName, driverPhone: $driverPhone, vendorId: $vendorId, notes: $notes) {
      id vehicleNumber vehicleType capacity driverName driverPhone status
    }
  }
`;

export const UPDATE_TRANSPORT_VEHICLE_MUTATION = gql`
  mutation UpdateTransportVehicle($id: ID!, $vehicleNumber: String, $vehicleType: String, $capacity: Int, $driverName: String, $driverPhone: String, $vendorId: ID, $status: String, $notes: String) {
    updateTransportVehicle(id: $id, vehicleNumber: $vehicleNumber, vehicleType: $vehicleType, capacity: $capacity, driverName: $driverName, driverPhone: $driverPhone, vendorId: $vendorId, status: $status, notes: $notes) {
      id vehicleNumber vehicleType capacity driverName driverPhone status
    }
  }
`;

export const DELETE_TRANSPORT_VEHICLE_MUTATION = gql`
  mutation DeleteTransportVehicle($id: ID!) { deleteTransportVehicle(id: $id) }
`;

export const ASSIGN_VEHICLE_MUTATION = gql`
  mutation AssignVehicle($vehicleId: ID!, $requestId: ID!, $notes: String) {
    assignVehicle(vehicleId: $vehicleId, requestId: $requestId, notes: $notes) {
      id vehicleId requestId assignedAt notes
      vehicle { id vehicleNumber vehicleType }
      request { id guestName status }
    }
  }
`;

export const UNASSIGN_VEHICLE_MUTATION = gql`
  mutation UnassignVehicle($id: ID!) { unassignVehicle(id: $id) }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Tasting Sessions (Module 6 Gap)
// ─────────────────────────────────────────────────────────────────────────────

export const MY_TASTING_SESSIONS_QUERY = gql`
  query MyTastingSessions($catererId: ID) {
    myTastingSessions(catererId: $catererId) {
      id userId catererId ceremonyId scheduledAt location status attendees feedback rating notes
      caterer { id name city cuisineTypes }
      ceremony { id name ceremonyType }
    }
  }
`;

export const CREATE_TASTING_SESSION_MUTATION = gql`
  mutation CreateTastingSession($catererId: ID!, $ceremonyId: ID, $scheduledAt: String, $location: String, $attendees: String, $notes: String) {
    createTastingSession(catererId: $catererId, ceremonyId: $ceremonyId, scheduledAt: $scheduledAt, location: $location, attendees: $attendees, notes: $notes) {
      id catererId ceremonyId scheduledAt location status
      caterer { id name } ceremony { id name }
    }
  }
`;

export const UPDATE_TASTING_SESSION_MUTATION = gql`
  mutation UpdateTastingSession($id: ID!, $scheduledAt: String, $location: String, $status: String, $attendees: String, $feedback: String, $rating: Float, $notes: String) {
    updateTastingSession(id: $id, scheduledAt: $scheduledAt, location: $location, status: $status, attendees: $attendees, feedback: $feedback, rating: $rating, notes: $notes) {
      id scheduledAt location status attendees feedback rating
    }
  }
`;

export const DELETE_TASTING_SESSION_MUTATION = gql`
  mutation DeleteTastingSession($id: ID!) { deleteTastingSession(id: $id) }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Guest RSVP Log (Module 7 Gap)
// ─────────────────────────────────────────────────────────────────────────────

export const GUEST_RSVP_LOG_QUERY = gql`
  query GuestRsvpLog($guestId: ID!) {
    guestRsvpLog(guestId: $guestId) {
      id guestId ceremonyId oldStatus newStatus changedBy changedAt notes
      ceremony { id name }
    }
  }
`;

export const LOG_RSVP_CHANGE_MUTATION = gql`
  mutation LogRsvpChange($guestId: ID!, $ceremonyId: ID, $oldStatus: String, $newStatus: String!, $notes: String) {
    logRsvpChange(guestId: $guestId, ceremonyId: $ceremonyId, oldStatus: $oldStatus, newStatus: $newStatus, notes: $notes) {
      id guestId newStatus changedAt
    }
  }
`;

// ════════════════════════════════════════════════════════════
// PHASE 4 — Event Scheduling, Budget, Notifications, Communication
// ════════════════════════════════════════════════════════════

// ── Timeline ─────────────────────────────────────────────────
export const MY_TIMELINE_EVENTS_QUERY = gql`
  query MyTimelineEvents($ceremonyId: ID) {
    myTimelineEvents(ceremonyId: $ceremonyId) {
      id userId ceremonyId title description eventType
      startTime endTime allDay status assignedTo dependsOn sortOrder color
      createdAt updatedAt
      ceremony { id name ceremonyType eventDate }
      tasks {
        id title status priority dueDate assignedRole assigneeName
      }
    }
  }
`;

export const TIMELINE_EVENT_QUERY = gql`
  query TimelineEvent($id: ID!) {
    timelineEvent(id: $id) {
      id title description eventType startTime endTime allDay
      status assignedTo dependsOn sortOrder color createdAt
      ceremony { id name }
      tasks { id title status priority dueDate assignedRole assigneeName notes }
    }
  }
`;

export const MY_TIMELINE_TASKS_QUERY = gql`
  query MyTimelineTasks($eventId: ID, $status: String) {
    myTimelineTasks(eventId: $eventId, status: $status) {
      id eventId title description assignedRole assigneeName
      dueDate reminderAt status priority notes createdAt
      event { id title ceremonyId }
    }
  }
`;

export const CREATE_TIMELINE_EVENT_MUTATION = gql`
  mutation CreateTimelineEvent(
    $title: String! $ceremonyId: ID $description: String $eventType: String
    $startTime: String $endTime: String $allDay: Boolean $assignedTo: String
    $dependsOn: ID $sortOrder: Int $color: String
  ) {
    createTimelineEvent(
      title: $title ceremonyId: $ceremonyId description: $description eventType: $eventType
      startTime: $startTime endTime: $endTime allDay: $allDay assignedTo: $assignedTo
      dependsOn: $dependsOn sortOrder: $sortOrder color: $color
    ) { id title eventType status startTime endTime }
  }
`;

export const UPDATE_TIMELINE_EVENT_MUTATION = gql`
  mutation UpdateTimelineEvent(
    $id: ID! $title: String $ceremonyId: ID $description: String $eventType: String
    $startTime: String $endTime: String $allDay: Boolean $status: String
    $assignedTo: String $dependsOn: ID $sortOrder: Int $color: String
  ) {
    updateTimelineEvent(
      id: $id title: $title ceremonyId: $ceremonyId description: $description
      eventType: $eventType startTime: $startTime endTime: $endTime allDay: $allDay
      status: $status assignedTo: $assignedTo dependsOn: $dependsOn
      sortOrder: $sortOrder color: $color
    ) { id title eventType status startTime endTime }
  }
`;

export const DELETE_TIMELINE_EVENT_MUTATION = gql`
  mutation DeleteTimelineEvent($id: ID!) { deleteTimelineEvent(id: $id) }
`;

export const CREATE_TIMELINE_TASK_MUTATION = gql`
  mutation CreateTimelineTask(
    $eventId: ID! $title: String! $description: String $assignedRole: String
    $assigneeName: String $dueDate: String $reminderAt: String
    $status: String $priority: String $notes: String
  ) {
    createTimelineTask(
      eventId: $eventId title: $title description: $description
      assignedRole: $assignedRole assigneeName: $assigneeName
      dueDate: $dueDate reminderAt: $reminderAt
      status: $status priority: $priority notes: $notes
    ) { id title status priority dueDate }
  }
`;

export const UPDATE_TIMELINE_TASK_MUTATION = gql`
  mutation UpdateTimelineTask(
    $id: ID! $title: String $description: String $assignedRole: String
    $assigneeName: String $dueDate: String $reminderAt: String
    $status: String $priority: String $notes: String
  ) {
    updateTimelineTask(
      id: $id title: $title description: $description
      assignedRole: $assignedRole assigneeName: $assigneeName
      dueDate: $dueDate reminderAt: $reminderAt
      status: $status priority: $priority notes: $notes
    ) { id title status priority dueDate }
  }
`;

export const DELETE_TIMELINE_TASK_MUTATION = gql`
  mutation DeleteTimelineTask($id: ID!) { deleteTimelineTask(id: $id) }
`;

// ── Budget ────────────────────────────────────────────────────
export const MY_BUDGET_CATEGORIES_QUERY = gql`
  query MyBudgetCategories {
    myBudgetCategories {
      id name description plannedAmount color sortOrder
      totalPlanned totalActual totalPaid createdAt
      serviceCategory fromService budgetAmount hasOwnBudget remaining usedPercent status
      creator { id email role { name displayName } }
      items {
        id title vendorName plannedAmount actualAmount paidAmount status dueDate notes ceremonyId fromBooking
        milestones { id title amount status dueDate paidDate }
      }
    }
  }
`;

export const MY_BUDGET_SUMMARY_QUERY = gql`
  query MyBudgetSummary {
    myBudgetSummary {
      totalBudget allocatedBudget totalPlanned totalActual totalPaid totalRemaining
      overBudget categoryCount itemCount overdueCount
      usedPercent status nearCount overCount noBudgetCount
    }
  }
`;

export const FAMILY_BUDGET_PLAN_QUERY = gql`
  query FamilyBudgetPlan($familyUserId: ID) {
    familyBudgetPlan(familyUserId: $familyUserId) { eventCity totalBudget serviceBudgets }
  }
`;

export const SET_TOTAL_BUDGET_MUTATION = gql`
  mutation SetTotalBudget($amount: Float!) {
    setTotalBudget(amount: $amount)
  }
`;

export const MY_PAYMENT_MILESTONES_QUERY = gql`
  query MyPaymentMilestones($status: String) {
    myPaymentMilestones(status: $status) {
      id budgetItemId title amount dueDate paidDate status notes createdAt
      budgetItem { id title vendorName category { id name } }
    }
  }
`;

export const CREATE_BUDGET_CATEGORY_MUTATION = gql`
  mutation CreateBudgetCategory($name: String $serviceCategory: String $description: String $plannedAmount: Float $color: String $sortOrder: Int) {
    createBudgetCategory(name: $name serviceCategory: $serviceCategory description: $description plannedAmount: $plannedAmount color: $color sortOrder: $sortOrder) {
      id name plannedAmount color
    }
  }
`;

export const UPDATE_BUDGET_CATEGORY_MUTATION = gql`
  mutation UpdateBudgetCategory($id: ID! $name: String $description: String $plannedAmount: Float $color: String $sortOrder: Int) {
    updateBudgetCategory(id: $id name: $name description: $description plannedAmount: $plannedAmount color: $color sortOrder: $sortOrder) {
      id name plannedAmount color
    }
  }
`;

export const DELETE_BUDGET_CATEGORY_MUTATION = gql`
  mutation DeleteBudgetCategory($id: ID!) { deleteBudgetCategory(id: $id) }
`;

export const CREATE_BUDGET_ITEM_MUTATION = gql`
  mutation CreateBudgetItem(
    $categoryId: ID! $title: String! $description: String $vendorName: String
    $ceremonyId: ID $plannedAmount: Float $actualAmount: Float $paidAmount: Float
    $status: String $dueDate: String $notes: String
  ) {
    createBudgetItem(
      categoryId: $categoryId title: $title description: $description vendorName: $vendorName
      ceremonyId: $ceremonyId plannedAmount: $plannedAmount actualAmount: $actualAmount
      paidAmount: $paidAmount status: $status dueDate: $dueDate notes: $notes
    ) { id title plannedAmount actualAmount paidAmount status }
  }
`;

export const UPDATE_BUDGET_ITEM_MUTATION = gql`
  mutation UpdateBudgetItem(
    $id: ID! $title: String $description: String $vendorName: String
    $ceremonyId: ID $plannedAmount: Float $actualAmount: Float $paidAmount: Float
    $status: String $dueDate: String $notes: String
  ) {
    updateBudgetItem(
      id: $id title: $title description: $description vendorName: $vendorName
      ceremonyId: $ceremonyId plannedAmount: $plannedAmount actualAmount: $actualAmount
      paidAmount: $paidAmount status: $status dueDate: $dueDate notes: $notes
    ) { id title plannedAmount actualAmount paidAmount status }
  }
`;

export const DELETE_BUDGET_ITEM_MUTATION = gql`
  mutation DeleteBudgetItem($id: ID!) { deleteBudgetItem(id: $id) }
`;

export const CREATE_PAYMENT_MILESTONE_MUTATION = gql`
  mutation CreatePaymentMilestone($budgetItemId: ID! $title: String! $amount: Float $dueDate: String $notes: String) {
    createPaymentMilestone(budgetItemId: $budgetItemId title: $title amount: $amount dueDate: $dueDate notes: $notes) {
      id title amount dueDate status
    }
  }
`;

export const UPDATE_PAYMENT_MILESTONE_MUTATION = gql`
  mutation UpdatePaymentMilestone($id: ID! $title: String $amount: Float $dueDate: String $paidDate: String $status: String $notes: String) {
    updatePaymentMilestone(id: $id title: $title amount: $amount dueDate: $dueDate paidDate: $paidDate status: $status notes: $notes) {
      id title amount dueDate paidDate status
    }
  }
`;

export const MARK_MILESTONE_AS_PAID_MUTATION = gql`
  mutation MarkMilestoneAsPaid($id: ID!) {
    markMilestoneAsPaid(id: $id) { id title amount status paidDate }
  }
`;

export const DELETE_PAYMENT_MILESTONE_MUTATION = gql`
  mutation DeletePaymentMilestone($id: ID!) { deletePaymentMilestone(id: $id) }
`;

// ── Notifications ─────────────────────────────────────────────
export const MY_NOTIFICATIONS_QUERY = gql`
  query MyNotifications($unreadOnly: Boolean) {
    myNotifications(unreadOnly: $unreadOnly) {
      id type title message isRead relatedEntityType recipients
      scheduledAt sentAt createdAt
      creator { id email }
    }
  }
`;

// Notification bell: only ids, always the signed-in user's own
export const MY_UNREAD_NOTIFICATIONS_QUERY = gql`
  query MyUnreadNotifications {
    myNotifications(unreadOnly: true) { id }
  }
`;

export const MY_NOTIFICATION_COUNT_QUERY = gql`
  query MyNotificationCount { myNotificationCount }
`;

export const MARK_NOTIFICATION_READ_MUTATION = gql`
  mutation MarkNotificationRead($id: ID!) {
    markNotificationRead(id: $id) { id isRead }
  }
`;

export const MARK_ALL_NOTIFICATIONS_READ_MUTATION = gql`
  mutation MarkAllNotificationsRead { markAllNotificationsRead }
`;

export const CREATE_NOTIFICATION_MUTATION = gql`
  mutation CreateNotification($userId: ID! $type: String $title: String! $message: String! $relatedEntityType: String $relatedEntityId: ID $scheduledAt: String) {
    createNotification(userId: $userId type: $type title: $title message: $message relatedEntityType: $relatedEntityType relatedEntityId: $relatedEntityId scheduledAt: $scheduledAt) {
      id type title message isRead
    }
  }
`;

export const BROADCAST_NOTIFICATION_MUTATION = gql`
  mutation BroadcastNotification($type: String $title: String! $message: String! $targetRole: String) {
    broadcastNotification(type: $type title: $title message: $message targetRole: $targetRole)
  }
`;

export const DELETE_NOTIFICATION_MUTATION = gql`
  mutation DeleteNotification($id: ID!) { deleteNotification(id: $id) }
`;

// ── Communication ─────────────────────────────────────────────
export const MY_COMMUNICATION_THREADS_QUERY = gql`
  query MyCommunicationThreads {
    myCommunicationThreads {
      id title description threadType isActive messageCount createdAt updatedAt
    }
  }
`;

export const COMMUNICATION_THREAD_QUERY = gql`
  query CommunicationThread($id: ID!) {
    communicationThread(id: $id) {
      id title description threadType isActive createdAt
      messages {
        id senderId message isEmergency createdAt
        sender { id email }
      }
    }
  }
`;

export const CREATE_COMMUNICATION_THREAD_MUTATION = gql`
  mutation CreateCommunicationThread($title: String! $description: String $threadType: String) {
    createCommunicationThread(title: $title description: $description threadType: $threadType) {
      id title threadType isActive
    }
  }
`;

export const UPDATE_COMMUNICATION_THREAD_MUTATION = gql`
  mutation UpdateCommunicationThread($id: ID! $title: String $description: String $threadType: String $isActive: Boolean) {
    updateCommunicationThread(id: $id title: $title description: $description threadType: $threadType isActive: $isActive) {
      id title threadType isActive
    }
  }
`;

export const DELETE_COMMUNICATION_THREAD_MUTATION = gql`
  mutation DeleteCommunicationThread($id: ID!) { deleteCommunicationThread(id: $id) }
`;

export const SEND_COMMUNICATION_MESSAGE_MUTATION = gql`
  mutation SendCommunicationMessage($threadId: ID! $message: String! $isEmergency: Boolean) {
    sendCommunicationMessage(threadId: $threadId message: $message isEmergency: $isEmergency) {
      id threadId message isEmergency createdAt
      sender { id email }
    }
  }
`;

export const DELETE_COMMUNICATION_MESSAGE_MUTATION = gql`
  mutation DeleteCommunicationMessage($id: ID!) { deleteCommunicationMessage(id: $id) }
`;

// ─────────────────────────────────────────────────────────────────────────────
// PHASE 5 — Honeymoon Module
// ─────────────────────────────────────────────────────────────────────────────

export const HONEYMOON_PARTNERS_QUERY = gql`
  query HoneymoonPartners($partnerType: String) {
    honeymoonPartners(partnerType: $partnerType) {
      id name partnerType description website phone email destinations isActive
    }
  }
`;

export const HONEYMOON_PACKAGES_QUERY = gql`
  query HoneymoonPackages($destination: String, $packageType: String) {
    honeymoonPackages(destination: $destination, packageType: $packageType) {
      id name destination durationDays basePrice packageType inclusions roomTypes description imageUrl isActive
      partner { id name partnerType phone email website }
    }
  }
`;

export const MY_HONEYMOON_BOOKINGS_QUERY = gql`
  query MyHoneymoonBookings {
    myHoneymoonBookings {
      id packageId partnerId bookingRef destination travelFrom travelTo roomType
      guestCount totalCost paidAmount customAddons status notes createdAt
      package { id name destination durationDays basePrice packageType inclusions roomTypes }
      partner { id name partnerType phone email }
      documents { id docType title fileUrl fileName notes createdAt }
    }
  }
`;

export const CREATE_HONEYMOON_BOOKING_MUTATION = gql`
  mutation CreateHoneymoonBooking($packageId: ID, $partnerId: ID, $destination: String, $travelFrom: String, $travelTo: String, $roomType: String, $guestCount: Int, $totalCost: Float, $customAddons: String, $notes: String) {
    createHoneymoonBooking(packageId: $packageId, partnerId: $partnerId, destination: $destination, travelFrom: $travelFrom, travelTo: $travelTo, roomType: $roomType, guestCount: $guestCount, totalCost: $totalCost, customAddons: $customAddons, notes: $notes) {
      id status destination
    }
  }
`;

export const UPDATE_HONEYMOON_BOOKING_MUTATION = gql`
  mutation UpdateHoneymoonBooking($id: ID!, $bookingRef: String, $destination: String, $travelFrom: String, $travelTo: String, $roomType: String, $guestCount: Int, $totalCost: Float, $paidAmount: Float, $customAddons: String, $status: String, $notes: String) {
    updateHoneymoonBooking(id: $id, bookingRef: $bookingRef, destination: $destination, travelFrom: $travelFrom, travelTo: $travelTo, roomType: $roomType, guestCount: $guestCount, totalCost: $totalCost, paidAmount: $paidAmount, customAddons: $customAddons, status: $status, notes: $notes) {
      id status
    }
  }
`;

export const DELETE_HONEYMOON_BOOKING_MUTATION = gql`
  mutation DeleteHoneymoonBooking($id: ID!) { deleteHoneymoonBooking(id: $id) }
`;

export const ADD_HONEYMOON_DOCUMENT_MUTATION = gql`
  mutation AddHoneymoonDocument($bookingId: ID!, $docType: String, $title: String!, $fileUrl: String, $fileName: String, $notes: String) {
    addHoneymoonDocument(bookingId: $bookingId, docType: $docType, title: $title, fileUrl: $fileUrl, fileName: $fileName, notes: $notes) {
      id docType title fileUrl fileName notes createdAt
    }
  }
`;

export const DELETE_HONEYMOON_DOCUMENT_MUTATION = gql`
  mutation DeleteHoneymoonDocument($id: ID!) { deleteHoneymoonDocument(id: $id) }
`;

// Admin mutations for partners and packages
export const CREATE_HONEYMOON_PARTNER_MUTATION = gql`
  mutation CreateHoneymoonPartner($name: String!, $partnerType: String, $description: String, $website: String, $phone: String, $email: String, $destinations: [String]) {
    createHoneymoonPartner(name: $name, partnerType: $partnerType, description: $description, website: $website, phone: $phone, email: $email, destinations: $destinations) { id name }
  }
`;
export const UPDATE_HONEYMOON_PARTNER_MUTATION = gql`
  mutation UpdateHoneymoonPartner($id: ID!, $name: String, $partnerType: String, $description: String, $website: String, $phone: String, $email: String, $destinations: [String], $isActive: Boolean) {
    updateHoneymoonPartner(id: $id, name: $name, partnerType: $partnerType, description: $description, website: $website, phone: $phone, email: $email, destinations: $destinations, isActive: $isActive) { id name isActive }
  }
`;
export const DELETE_HONEYMOON_PARTNER_MUTATION = gql`
  mutation DeleteHoneymoonPartner($id: ID!) { deleteHoneymoonPartner(id: $id) }
`;
export const CREATE_HONEYMOON_PACKAGE_MUTATION = gql`
  mutation CreateHoneymoonPackage($partnerId: ID!, $name: String!, $destination: String!, $durationDays: Int, $basePrice: Float, $packageType: String, $inclusions: [String], $roomTypes: [String], $description: String) {
    createHoneymoonPackage(partnerId: $partnerId, name: $name, destination: $destination, durationDays: $durationDays, basePrice: $basePrice, packageType: $packageType, inclusions: $inclusions, roomTypes: $roomTypes, description: $description) { id name }
  }
`;
export const UPDATE_HONEYMOON_PACKAGE_MUTATION = gql`
  mutation UpdateHoneymoonPackage($id: ID!, $name: String, $destination: String, $durationDays: Int, $basePrice: Float, $packageType: String, $inclusions: [String], $roomTypes: [String], $description: String, $isActive: Boolean) {
    updateHoneymoonPackage(id: $id, name: $name, destination: $destination, durationDays: $durationDays, basePrice: $basePrice, packageType: $packageType, inclusions: $inclusions, roomTypes: $roomTypes, description: $description, isActive: $isActive) { id name }
  }
`;
export const DELETE_HONEYMOON_PACKAGE_MUTATION = gql`
  mutation DeleteHoneymoonPackage($id: ID!) { deleteHoneymoonPackage(id: $id) }
`;

// ─────────────────────────────────────────────────────────────────────────────
// PHASE 5 — Stage & A/V Setup
// ─────────────────────────────────────────────────────────────────────────────

export const MY_STAGE_SETUPS_QUERY = gql`
  query MyStageSetups($ceremonyId: ID) {
    myStageSetups(ceremonyId: $ceremonyId) {
      id setupName stageType stageDimensions backdropTheme lightingType soundSystem
      screensCount projectorsCount microphonesCount liveStreaming setupCost status notes
      ceremonyId vendorId
      ceremony { id name }
      vendor { id name }
      createdAt updatedAt
    }
  }
`;

export const CREATE_STAGE_SETUP_MUTATION = gql`
  mutation CreateStageSetup($ceremonyId: ID, $setupName: String!, $stageType: String, $stageDimensions: String, $backdropTheme: String, $lightingType: String, $soundSystem: String, $screensCount: Int, $projectorsCount: Int, $microphonesCount: Int, $liveStreaming: Boolean, $vendorId: ID, $setupCost: Float, $notes: String) {
    createStageSetup(ceremonyId: $ceremonyId, setupName: $setupName, stageType: $stageType, stageDimensions: $stageDimensions, backdropTheme: $backdropTheme, lightingType: $lightingType, soundSystem: $soundSystem, screensCount: $screensCount, projectorsCount: $projectorsCount, microphonesCount: $microphonesCount, liveStreaming: $liveStreaming, vendorId: $vendorId, setupCost: $setupCost, notes: $notes) {
      id setupName status
    }
  }
`;

export const UPDATE_STAGE_SETUP_MUTATION = gql`
  mutation UpdateStageSetup($id: ID!, $setupName: String, $stageType: String, $stageDimensions: String, $backdropTheme: String, $lightingType: String, $soundSystem: String, $screensCount: Int, $projectorsCount: Int, $microphonesCount: Int, $liveStreaming: Boolean, $vendorId: ID, $setupCost: Float, $status: String, $notes: String) {
    updateStageSetup(id: $id, setupName: $setupName, stageType: $stageType, stageDimensions: $stageDimensions, backdropTheme: $backdropTheme, lightingType: $lightingType, soundSystem: $soundSystem, screensCount: $screensCount, projectorsCount: $projectorsCount, microphonesCount: $microphonesCount, liveStreaming: $liveStreaming, vendorId: $vendorId, setupCost: $setupCost, status: $status, notes: $notes) {
      id setupName status
    }
  }
`;

export const DELETE_STAGE_SETUP_MUTATION = gql`
  mutation DeleteStageSetup($id: ID!) { deleteStageSetup(id: $id) }
`;

// ─── Phase 5 New Modules ────────────────────────────────────────────────────

// Photo Albums
export const MY_PHOTO_ALBUMS_QUERY = gql`
  query MyPhotoAlbums {
    myPhotoAlbums {
      id name albumType status totalPhotos coverPhotoUrl shareUrl description deliveryDate vendorName vendorContact ceremonyId notes
      photos { id url thumbnail caption isCover isFavourite sortOrder tags }
    }
  }
`;
export const CREATE_PHOTO_ALBUM_MUTATION = gql`
  mutation CreatePhotoAlbum($name: String!, $albumType: String, $ceremonyId: ID, $vendorName: String, $vendorContact: String, $deliveryDate: String, $shareUrl: String, $description: String, $notes: String) {
    createPhotoAlbum(name: $name, albumType: $albumType, ceremonyId: $ceremonyId, vendorName: $vendorName, vendorContact: $vendorContact, deliveryDate: $deliveryDate, shareUrl: $shareUrl, description: $description, notes: $notes) { id name status }
  }
`;
export const UPDATE_PHOTO_ALBUM_MUTATION = gql`
  mutation UpdatePhotoAlbum($id: ID!, $name: String, $albumType: String, $status: String, $vendorName: String, $vendorContact: String, $deliveryDate: String, $shareUrl: String, $description: String, $notes: String) {
    updatePhotoAlbum(id: $id, name: $name, albumType: $albumType, status: $status, vendorName: $vendorName, vendorContact: $vendorContact, deliveryDate: $deliveryDate, shareUrl: $shareUrl, description: $description, notes: $notes) { id name status }
  }
`;
export const DELETE_PHOTO_ALBUM_MUTATION = gql`
  mutation DeletePhotoAlbum($id: ID!) { deletePhotoAlbum(id: $id) }
`;
export const ADD_ALBUM_PHOTO_MUTATION = gql`
  mutation AddAlbumPhoto($albumId: ID!, $url: String!, $thumbnail: String, $caption: String, $isCover: Boolean, $tags: [String]) {
    addAlbumPhoto(albumId: $albumId, url: $url, thumbnail: $thumbnail, caption: $caption, isCover: $isCover, tags: $tags) { id url caption isCover isFavourite }
  }
`;
export const UPDATE_ALBUM_PHOTO_MUTATION = gql`
  mutation UpdateAlbumPhoto($id: ID!, $caption: String, $isCover: Boolean, $isFavourite: Boolean, $sortOrder: Int, $tags: [String]) {
    updateAlbumPhoto(id: $id, caption: $caption, isCover: $isCover, isFavourite: $isFavourite, sortOrder: $sortOrder, tags: $tags) { id caption isFavourite }
  }
`;
export const DELETE_ALBUM_PHOTO_MUTATION = gql`
  mutation DeleteAlbumPhoto($id: ID!) { deleteAlbumPhoto(id: $id) }
`;

// Invitations
export const INVITATION_TEMPLATES_QUERY = gql`
  query InvitationTemplates {
    invitationTemplates { id name style language eventType bodyHtml subjectTemplate thumbnailUrl isSystem isActive notes }
  }
`;
// Invitations: sent by WhatsApp and email with the family's details; each guest replies on
// their own invitation page
const INVITATION_RECIPIENT_FIELDS = `
  id guestId guestName email phone dispatchMethod sentAt openedAt rsvpResponse notes
  emailStatus emailSentAt whatsappStatus whatsappSentAt lastError rsvpAt guestsCount rsvpNote
  inviteLink whatsappLink
`;
export const MY_INVITATIONS_QUERY = gql`
  query MyInvitations {
    myInvitations {
      id title subject customMessage dispatchMethod status ceremonyId templateId notes details sentAt totalOpened
      template { id name }
      recipients { ${INVITATION_RECIPIENT_FIELDS} }
    }
  }
`;
export const INVITATION_CHANNELS_QUERY = gql`
  query InvitationChannels { invitationChannels { emailReady whatsappAutomatic whatsappBusinessNumber } }
`;
export const INVITATION_PREVIEW_QUERY = gql`
  query InvitationPreview($invitationId: ID!) {
    invitationPreview(invitationId: $invitationId) { guestName whatsappText emailSubject emailHtml }
  }
`;
export const CREATE_INVITATION_MUTATION = gql`
  mutation CreateInvitation($title: String!, $ceremonyId: ID, $templateId: ID, $subject: String, $customMessage: String, $dispatchMethod: String, $notes: String, $details: JSON) {
    createInvitation(title: $title, ceremonyId: $ceremonyId, templateId: $templateId, subject: $subject, customMessage: $customMessage, dispatchMethod: $dispatchMethod, notes: $notes, details: $details) { id title status }
  }
`;
export const UPDATE_INVITATION_MUTATION = gql`
  mutation UpdateInvitation($id: ID!, $title: String, $ceremonyId: ID, $templateId: ID, $subject: String, $customMessage: String, $dispatchMethod: String, $status: String, $notes: String, $details: JSON) {
    updateInvitation(id: $id, title: $title, ceremonyId: $ceremonyId, templateId: $templateId, subject: $subject, customMessage: $customMessage, dispatchMethod: $dispatchMethod, status: $status, notes: $notes, details: $details) { id title status }
  }
`;
export const DELETE_INVITATION_MUTATION = gql`
  mutation DeleteInvitation($id: ID!) { deleteInvitation(id: $id) }
`;
export const ADD_INVITATION_RECIPIENT_MUTATION = gql`
  mutation AddInvitationRecipient($invitationId: ID!, $guestName: String!, $email: String, $phone: String, $dispatchMethod: String, $notes: String) {
    addInvitationRecipient(invitationId: $invitationId, guestName: $guestName, email: $email, phone: $phone, dispatchMethod: $dispatchMethod, notes: $notes) { id guestName }
  }
`;
export const ADD_INVITATION_GUESTS_MUTATION = gql`
  mutation AddInvitationGuests($invitationId: ID!, $guestIds: [ID!]!, $dispatchMethod: String) {
    addInvitationRecipientsFromGuests(invitationId: $invitationId, guestIds: $guestIds, dispatchMethod: $dispatchMethod)
  }
`;
export const DELETE_INVITATION_RECIPIENT_MUTATION = gql`
  mutation DeleteInvitationRecipient($id: ID!) { deleteInvitationRecipient(id: $id) }
`;
export const SEND_INVITATION_MUTATION = gql`
  mutation SendInvitation($id: ID!, $recipientIds: [ID!], $resend: Boolean, $guestConsent: Boolean) {
    sendInvitation(id: $id, recipientIds: $recipientIds, resend: $resend, guestConsent: $guestConsent) {
      emailed emailFailed whatsappSent whatsappFailed whatsappManual skipped whatsappAutomatic
      invitation { id status sentAt }
    }
  }
`;
export const MARK_INVITATION_WHATSAPP_SENT_MUTATION = gql`
  mutation MarkInvitationWhatsAppSent($recipientId: ID!) {
    markInvitationWhatsAppSent(recipientId: $recipientId) { ${INVITATION_RECIPIENT_FIELDS} }
  }
`;
const WHATSAPP_SETTINGS_FIELDS = 'configured isActive phoneNumberId businessNumber apiVersion templateName templateLanguage hasAccessToken templateGuide';
export const WHATSAPP_SETTINGS_QUERY = gql`
  query WhatsAppSettings { whatsappSettings { ${WHATSAPP_SETTINGS_FIELDS} } }
`;
export const SAVE_WHATSAPP_SETTINGS_MUTATION = gql`
  mutation SaveWhatsAppSettings($input: WhatsAppSettingsInput!) { saveWhatsAppSettings(input: $input) { ${WHATSAPP_SETTINGS_FIELDS} } }
`;
export const TEST_WHATSAPP_MUTATION = gql`
  mutation TestWhatsApp($to: String!, $templateId: ID) { testWhatsApp(to: $to, templateId: $templateId) }
`;
export const CREATE_INVITATION_TEMPLATE_MUTATION = gql`
  mutation CreateInvitationTemplate($name: String!, $style: String, $language: String, $eventType: String, $bodyHtml: String!, $subjectTemplate: String, $thumbnailUrl: String, $isActive: Boolean, $notes: String) {
    createInvitationTemplate(name: $name, style: $style, language: $language, eventType: $eventType, bodyHtml: $bodyHtml, subjectTemplate: $subjectTemplate, thumbnailUrl: $thumbnailUrl, isActive: $isActive, notes: $notes) { id name }
  }
`;

// ── Wedding Services ──────────────────────────────────────────────────────────
export const WEDDING_SERVICES_QUERY = gql`
  query WeddingServices($category: String, $activeOnly: Boolean) {
    weddingServices(category: $category, activeOnly: $activeOnly) {
      id name category description unit basePrice taxRate maxDiscount isActive imageUrl tags createdAt
    }
  }
`;
export const SERVICE_CATEGORIES_QUERY = gql`
  query ServiceCategories { serviceCategories }
`;
export const ALL_SERVICE_CATEGORIES_QUERY = gql`
  query AllServiceCategories($activeOnly: Boolean) {
    allServiceCategories(activeOnly: $activeOnly) {
      id name displayName description icon color sortOrder isActive createdAt updatedAt
    }
  }
`;
export const CREATE_SERVICE_CATEGORY_MUTATION = gql`
  mutation CreateServiceCategory($input: CreateServiceCategoryInput!) {
    createServiceCategory(input: $input) {
      id name displayName description icon color sortOrder isActive createdAt updatedAt
    }
  }
`;
export const UPDATE_SERVICE_CATEGORY_MUTATION = gql`
  mutation UpdateServiceCategory($input: UpdateServiceCategoryInput!) {
    updateServiceCategory(input: $input) {
      id name displayName description icon color sortOrder isActive createdAt updatedAt
    }
  }
`;
export const DELETE_SERVICE_CATEGORY_MUTATION = gql`
  mutation DeleteServiceCategory($id: ID!) { deleteServiceCategory(id: $id) }
`;
export const MY_CONTRACT_SERVICE_CATEGORIES_QUERY = gql`
  query MyContractServiceCategories { myContractServiceCategories }
`;
export const CREATE_WEDDING_SERVICE_MUTATION = gql`
  mutation CreateWeddingService($input: CreateWeddingServiceInput!) {
    createWeddingService(input: $input) {
      id name category description unit basePrice taxRate maxDiscount isActive imageUrl tags createdAt
    }
  }
`;
export const UPDATE_WEDDING_SERVICE_MUTATION = gql`
  mutation UpdateWeddingService($input: UpdateWeddingServiceInput!) {
    updateWeddingService(input: $input) {
      id name category description unit basePrice taxRate maxDiscount isActive imageUrl tags
    }
  }
`;
export const DELETE_WEDDING_SERVICE_MUTATION = gql`
  mutation DeleteWeddingService($id: ID!) { deleteWeddingService(id: $id) }
`;

// ── Contract Service Items ────────────────────────────────────────────────────
export const CONTRACT_SERVICE_ITEMS_QUERY = gql`
  query ContractServiceItems($contractId: ID!) {
    contractServiceItems(contractId: $contractId) {
      id contractId serviceId serviceName serviceCategory unit
      quantity unitPrice discountType discountValue discountAmount
      taxRate taxAmount subtotal lineTotal notes sortOrder addedBy isApproved approvedAt createdAt
      service { id name category basePrice unit }
    }
  }
`;
export const ADD_CONTRACT_SERVICE_ITEM_MUTATION = gql`
  mutation AddContractServiceItem($input: AddContractServiceItemInput!) {
    addContractServiceItem(input: $input) {
      id contractId serviceId serviceName serviceCategory unit
      quantity unitPrice discountType discountValue discountAmount
      taxRate taxAmount subtotal lineTotal notes sortOrder addedBy isApproved createdAt
      service { id name category }
    }
  }
`;
export const UPDATE_CONTRACT_SERVICE_ITEM_MUTATION = gql`
  mutation UpdateContractServiceItem($input: UpdateContractServiceItemInput!) {
    updateContractServiceItem(input: $input) {
      id quantity unitPrice discountType discountValue discountAmount taxRate taxAmount subtotal lineTotal notes sortOrder
    }
  }
`;
export const REMOVE_CONTRACT_SERVICE_ITEM_MUTATION = gql`
  mutation RemoveContractServiceItem($id: ID!) { removeContractServiceItem(id: $id) }
`;
export const APPROVE_CONTRACT_SERVICE_ITEM_MUTATION = gql`
  mutation ApproveContractServiceItem($id: ID!) {
    approveContractServiceItem(id: $id) { id isApproved approvedAt }
  }
`;
export const BULK_ADD_CONTRACT_SERVICE_ITEMS_MUTATION = gql`
  mutation BulkAddContractServiceItems($contractId: ID!, $serviceIds: [ID!]!) {
    bulkAddContractServiceItems(contractId: $contractId, serviceIds: $serviceIds) {
      id serviceName serviceCategory quantity unitPrice lineTotal
    }
  }
`;
export const UPDATE_INVITATION_TEMPLATE_MUTATION = gql`
  mutation UpdateInvitationTemplate($id: ID!, $name: String, $style: String, $language: String, $eventType: String, $bodyHtml: String, $subjectTemplate: String, $thumbnailUrl: String, $isActive: Boolean, $notes: String) {
    updateInvitationTemplate(id: $id, name: $name, style: $style, language: $language, eventType: $eventType, bodyHtml: $bodyHtml, subjectTemplate: $subjectTemplate, thumbnailUrl: $thumbnailUrl, isActive: $isActive, notes: $notes) { id name }
  }
`;
export const DELETE_INVITATION_TEMPLATE_MUTATION = gql`
  mutation DeleteInvitationTemplate($id: ID!) { deleteInvitationTemplate(id: $id) }
`;

// Accommodation
export const MY_ACCOMMODATION_BLOCKS_QUERY = gql`
  query MyAccommodationBlocks {
    myAccommodationBlocks {
      id hotelName address city starRating contactName contactPhone contactEmail roomsBlocked roomsAllocated pricePerNight checkInDate checkOutDate shuttleAvailable shuttleSchedule specialRateCode bookingDeadline status ceremonyId notes
      roomAllocations { id guestName roomType roomNumber checkIn checkOut numGuests status paidBy dietaryPref specialRequests notes }
    }
  }
`;
export const CREATE_ACCOMMODATION_BLOCK_MUTATION = gql`
  mutation CreateAccommodationBlock($hotelName: String!, $address: String, $city: String, $starRating: Int, $contactName: String, $contactPhone: String, $contactEmail: String, $roomsBlocked: Int!, $pricePerNight: Float, $checkInDate: String, $checkOutDate: String, $shuttleAvailable: Boolean, $shuttleSchedule: String, $specialRateCode: String, $bookingDeadline: String, $ceremonyId: ID, $notes: String) {
    createAccommodationBlock(hotelName: $hotelName, address: $address, city: $city, starRating: $starRating, contactName: $contactName, contactPhone: $contactPhone, contactEmail: $contactEmail, roomsBlocked: $roomsBlocked, pricePerNight: $pricePerNight, checkInDate: $checkInDate, checkOutDate: $checkOutDate, shuttleAvailable: $shuttleAvailable, shuttleSchedule: $shuttleSchedule, specialRateCode: $specialRateCode, bookingDeadline: $bookingDeadline, ceremonyId: $ceremonyId, notes: $notes) { id hotelName status }
  }
`;
export const UPDATE_ACCOMMODATION_BLOCK_MUTATION = gql`
  mutation UpdateAccommodationBlock($id: ID!, $hotelName: String, $address: String, $city: String, $starRating: Int, $contactName: String, $contactPhone: String, $contactEmail: String, $roomsBlocked: Int, $pricePerNight: Float, $checkInDate: String, $checkOutDate: String, $shuttleAvailable: Boolean, $shuttleSchedule: String, $specialRateCode: String, $bookingDeadline: String, $status: String, $notes: String) {
    updateAccommodationBlock(id: $id, hotelName: $hotelName, address: $address, city: $city, starRating: $starRating, contactName: $contactName, contactPhone: $contactPhone, contactEmail: $contactEmail, roomsBlocked: $roomsBlocked, pricePerNight: $pricePerNight, checkInDate: $checkInDate, checkOutDate: $checkOutDate, shuttleAvailable: $shuttleAvailable, shuttleSchedule: $shuttleSchedule, specialRateCode: $specialRateCode, bookingDeadline: $bookingDeadline, status: $status, notes: $notes) { id hotelName status }
  }
`;
export const DELETE_ACCOMMODATION_BLOCK_MUTATION = gql`
  mutation DeleteAccommodationBlock($id: ID!) { deleteAccommodationBlock(id: $id) }
`;
export const ASSIGN_GUEST_ROOM_MUTATION = gql`
  mutation AssignGuestRoom($blockId: ID!, $guestName: String!, $roomType: String, $roomNumber: String, $checkIn: String, $checkOut: String, $numGuests: Int, $paidBy: String, $dietaryPref: String, $specialRequests: String, $notes: String) {
    assignGuestRoom(blockId: $blockId, guestName: $guestName, roomType: $roomType, roomNumber: $roomNumber, checkIn: $checkIn, checkOut: $checkOut, numGuests: $numGuests, paidBy: $paidBy, dietaryPref: $dietaryPref, specialRequests: $specialRequests, notes: $notes) { id guestName status }
  }
`;
export const UPDATE_GUEST_ACCOMMODATION_MUTATION = gql`
  mutation UpdateGuestAccommodation($id: ID!, $guestName: String, $roomType: String, $roomNumber: String, $checkIn: String, $checkOut: String, $numGuests: Int, $status: String, $paidBy: String, $dietaryPref: String, $specialRequests: String, $notes: String) {
    updateGuestAccommodation(id: $id, guestName: $guestName, roomType: $roomType, roomNumber: $roomNumber, checkIn: $checkIn, checkOut: $checkOut, numGuests: $numGuests, status: $status, paidBy: $paidBy, dietaryPref: $dietaryPref, specialRequests: $specialRequests, notes: $notes) { id guestName status }
  }
`;
export const DELETE_GUEST_ACCOMMODATION_MUTATION = gql`
  mutation DeleteGuestAccommodation($id: ID!) { deleteGuestAccommodation(id: $id) }
`;

// Gift Registry
export const MY_GIFT_REGISTRY_QUERY = gql`
  query MyGiftRegistryItems {
    myGiftRegistryItems { id name category description preferredBrand priceEstimate productUrl priority quantityNeeded quantityReceived isFulfilled notes }
  }
`;
export const MY_GIFTS_RECEIVED_QUERY = gql`
  query MyGiftsReceived {
    myGiftsReceived { id fromGuestName itemDescription category giftType valueEstimate receivedAt thankyouSent thankyouMessage ceremonyId notes registryItemId }
  }
`;
export const MY_GIFT_SUMMARY_QUERY = gql`
  query MyGiftSummary {
    myGiftSummary { totalItems fulfilledItems totalGiftsReceived totalValue thankYouPending }
  }
`;
export const CREATE_GIFT_REGISTRY_ITEM_MUTATION = gql`
  mutation CreateGiftRegistryItem($name: String!, $category: String, $description: String, $preferredBrand: String, $priceEstimate: Float, $productUrl: String, $priority: String, $quantityNeeded: Int, $notes: String) {
    createGiftRegistryItem(name: $name, category: $category, description: $description, preferredBrand: $preferredBrand, priceEstimate: $priceEstimate, productUrl: $productUrl, priority: $priority, quantityNeeded: $quantityNeeded, notes: $notes) { id name }
  }
`;
export const UPDATE_GIFT_REGISTRY_ITEM_MUTATION = gql`
  mutation UpdateGiftRegistryItem($id: ID!, $name: String, $category: String, $description: String, $preferredBrand: String, $priceEstimate: Float, $productUrl: String, $priority: String, $quantityNeeded: Int, $notes: String) {
    updateGiftRegistryItem(id: $id, name: $name, category: $category, description: $description, preferredBrand: $preferredBrand, priceEstimate: $priceEstimate, productUrl: $productUrl, priority: $priority, quantityNeeded: $quantityNeeded, notes: $notes) { id name }
  }
`;
export const DELETE_GIFT_REGISTRY_ITEM_MUTATION = gql`
  mutation DeleteGiftRegistryItem($id: ID!) { deleteGiftRegistryItem(id: $id) }
`;
export const RECORD_GIFT_RECEIVED_MUTATION = gql`
  mutation RecordGiftReceived($fromGuestName: String!, $itemDescription: String!, $category: String, $giftType: String, $valueEstimate: Float, $receivedAt: String, $ceremonyId: ID, $registryItemId: ID, $notes: String) {
    recordGiftReceived(fromGuestName: $fromGuestName, itemDescription: $itemDescription, category: $category, giftType: $giftType, valueEstimate: $valueEstimate, receivedAt: $receivedAt, ceremonyId: $ceremonyId, registryItemId: $registryItemId, notes: $notes) { id fromGuestName }
  }
`;
export const UPDATE_GIFT_RECEIVED_MUTATION = gql`
  mutation UpdateGiftReceived($id: ID!, $itemDescription: String, $valueEstimate: Float, $thankyouSent: Boolean, $thankyouMessage: String, $notes: String) {
    updateGiftReceived(id: $id, itemDescription: $itemDescription, valueEstimate: $valueEstimate, thankyouSent: $thankyouSent, thankyouMessage: $thankyouMessage, notes: $notes) { id thankyouSent }
  }
`;
export const DELETE_GIFT_RECEIVED_MUTATION = gql`
  mutation DeleteGiftReceived($id: ID!) { deleteGiftReceived(id: $id) }
`;

// Beauty Services
export const BEAUTY_SERVICE_PROVIDERS_QUERY = gql`
  query BeautyServiceProviders($serviceType: String, $city: String) {
    beautyServiceProviders(serviceType: $serviceType, city: $city) { id name contactName phone email city address description serviceTypes priceMin priceMax rating instagramUrl websiteUrl portfolioUrl notes }
  }
`;
export const MY_BEAUTY_BOOKINGS_QUERY = gql`
  query MyBeautyBookings {
    myBeautyBookings { id serviceType providerId status eventDate startTime endTime location numPersons agreedPrice advancePaid trialDate trialDone specialRequests notes
      provider { id name }
    }
  }
`;
export const CREATE_BEAUTY_SERVICE_PROVIDER_MUTATION = gql`
  mutation CreateBeautyServiceProvider($name: String!, $contactName: String, $phone: String, $email: String, $city: String, $address: String, $description: String, $serviceTypes: [String], $priceMin: Float, $priceMax: Float, $rating: Float, $instagramUrl: String, $websiteUrl: String, $portfolioUrl: String, $notes: String) {
    createBeautyServiceProvider(name: $name, contactName: $contactName, phone: $phone, email: $email, city: $city, address: $address, description: $description, serviceTypes: $serviceTypes, priceMin: $priceMin, priceMax: $priceMax, rating: $rating, instagramUrl: $instagramUrl, websiteUrl: $websiteUrl, portfolioUrl: $portfolioUrl, notes: $notes) { id name }
  }
`;
export const UPDATE_BEAUTY_SERVICE_PROVIDER_MUTATION = gql`
  mutation UpdateBeautyServiceProvider($id: ID!, $name: String, $contactName: String, $phone: String, $email: String, $city: String, $address: String, $description: String, $serviceTypes: [String], $priceMin: Float, $priceMax: Float, $rating: Float, $instagramUrl: String, $websiteUrl: String, $portfolioUrl: String, $notes: String) {
    updateBeautyServiceProvider(id: $id, name: $name, contactName: $contactName, phone: $phone, email: $email, city: $city, address: $address, description: $description, serviceTypes: $serviceTypes, priceMin: $priceMin, priceMax: $priceMax, rating: $rating, instagramUrl: $instagramUrl, websiteUrl: $websiteUrl, portfolioUrl: $portfolioUrl, notes: $notes) { id name }
  }
`;
export const DELETE_BEAUTY_SERVICE_PROVIDER_MUTATION = gql`
  mutation DeleteBeautyServiceProvider($id: ID!) { deleteBeautyServiceProvider(id: $id) }
`;
export const CREATE_BEAUTY_BOOKING_MUTATION = gql`
  mutation CreateBeautyBooking($providerId: ID!, $serviceType: String!, $ceremonyId: ID, $eventDate: String, $startTime: String, $endTime: String, $location: String, $numPersons: Int, $agreedPrice: Float, $advancePaid: Float, $trialDate: String, $specialRequests: String, $notes: String) {
    createBeautyBooking(providerId: $providerId, serviceType: $serviceType, ceremonyId: $ceremonyId, eventDate: $eventDate, startTime: $startTime, endTime: $endTime, location: $location, numPersons: $numPersons, agreedPrice: $agreedPrice, advancePaid: $advancePaid, trialDate: $trialDate, specialRequests: $specialRequests, notes: $notes) { id serviceType status }
  }
`;
export const UPDATE_BEAUTY_BOOKING_MUTATION = gql`
  mutation UpdateBeautyBooking($id: ID!, $serviceType: String, $ceremonyId: ID, $eventDate: String, $startTime: String, $endTime: String, $location: String, $numPersons: Int, $agreedPrice: Float, $advancePaid: Float, $trialDate: String, $trialDone: Boolean, $status: String, $specialRequests: String, $notes: String) {
    updateBeautyBooking(id: $id, serviceType: $serviceType, ceremonyId: $ceremonyId, eventDate: $eventDate, startTime: $startTime, endTime: $endTime, location: $location, numPersons: $numPersons, agreedPrice: $agreedPrice, advancePaid: $advancePaid, trialDate: $trialDate, trialDone: $trialDone, status: $status, specialRequests: $specialRequests, notes: $notes) { id status }
  }
`;
export const DELETE_BEAUTY_BOOKING_MUTATION = gql`
  mutation DeleteBeautyBooking($id: ID!) { deleteBeautyBooking(id: $id) }
`;

// Astrology
export const ASTROLOGY_CONSULTANTS_QUERY = gql`
  query AstrologyConsultants($specialisations: [String], $city: String) {
    astrologyConsultants(specialisations: $specialisations, city: $city) { id name contactPhone email city specialisations languagesSpoken yearsExperience pricePerSession totalConsultations isOnline isVerified description notes }
  }
`;
export const MY_ASTROLOGY_CONSULTATIONS_QUERY = gql`
  query MyAstrologyConsultations {
    myAstrologyConsultations { id consultantId purposeType ceremonyId consultationDate consultationTime duration mode status muhurthamDatetime reportUrl notes
      consultant { id name }
    }
  }
`;
export const CREATE_ASTROLOGY_CONSULTATION_MUTATION = gql`
  mutation CreateAstrologyConsultation($consultantId: ID!, $purposeType: String!, $ceremonyId: ID, $consultationDate: String, $consultationTime: String, $duration: Int, $mode: String, $muhurthamDatetime: String, $notes: String) {
    createAstrologyConsultation(consultantId: $consultantId, purposeType: $purposeType, ceremonyId: $ceremonyId, consultationDate: $consultationDate, consultationTime: $consultationTime, duration: $duration, mode: $mode, muhurthamDatetime: $muhurthamDatetime, notes: $notes) { id purposeType status }
  }
`;
export const UPDATE_ASTROLOGY_CONSULTATION_MUTATION = gql`
  mutation UpdateAstrologyConsultation($id: ID!, $purposeType: String, $ceremonyId: ID, $consultationDate: String, $consultationTime: String, $duration: Int, $mode: String, $status: String, $muhurthamDatetime: String, $reportUrl: String, $notes: String) {
    updateAstrologyConsultation(id: $id, purposeType: $purposeType, ceremonyId: $ceremonyId, consultationDate: $consultationDate, consultationTime: $consultationTime, duration: $duration, mode: $mode, status: $status, muhurthamDatetime: $muhurthamDatetime, reportUrl: $reportUrl, notes: $notes) { id status }
  }
`;
export const CREATE_ASTROLOGY_CONSULTANT_MUTATION = gql`
  mutation CreateAstrologyConsultant($name: String!, $title: String, $specialisations: [String], $city: String, $state: String, $contactPhone: String, $email: String, $website: String, $languagesSpoken: [String], $yearsExperience: Int, $pricePerSession: Float, $isOnline: Boolean, $isVerified: Boolean, $description: String, $notes: String) {
    createAstrologyConsultant(name: $name, title: $title, specialisations: $specialisations, city: $city, state: $state, contactPhone: $contactPhone, email: $email, website: $website, languagesSpoken: $languagesSpoken, yearsExperience: $yearsExperience, pricePerSession: $pricePerSession, isOnline: $isOnline, isVerified: $isVerified, description: $description, notes: $notes) { id name }
  }
`;
export const UPDATE_ASTROLOGY_CONSULTANT_MUTATION = gql`
  mutation UpdateAstrologyConsultant($id: ID!, $name: String, $title: String, $specialisations: [String], $city: String, $state: String, $contactPhone: String, $email: String, $website: String, $languagesSpoken: [String], $yearsExperience: Int, $pricePerSession: Float, $isOnline: Boolean, $isVerified: Boolean, $rating: Float, $isActive: Boolean, $description: String, $notes: String) {
    updateAstrologyConsultant(id: $id, name: $name, title: $title, specialisations: $specialisations, city: $city, state: $state, contactPhone: $contactPhone, email: $email, website: $website, languagesSpoken: $languagesSpoken, yearsExperience: $yearsExperience, pricePerSession: $pricePerSession, isOnline: $isOnline, isVerified: $isVerified, rating: $rating, isActive: $isActive, description: $description, notes: $notes) { id name }
  }
`;
export const DELETE_ASTROLOGY_CONSULTANT_MUTATION = gql`
  mutation DeleteAstrologyConsultant($id: ID!) { deleteAstrologyConsultant(id: $id) }
`;
export const DELETE_ASTROLOGY_CONSULTATION_MUTATION = gql`
  mutation DeleteAstrologyConsultation($id: ID!) { deleteAstrologyConsultation(id: $id) }
`;

// ── Admin Cross-Phase Queries & Mutations ─────────────────────
// Phase 2 — Decoration Themes CRUD (admin)
export const CREATE_DECORATION_THEME_MUTATION = gql`
  mutation CreateDecorationTheme($name: String!, $style: String, $colorPalette: [String], $description: String, $estimatedCost: Float, $imageUrl: String) {
    createDecorationTheme(name: $name, style: $style, colorPalette: $colorPalette, description: $description, estimatedCost: $estimatedCost, imageUrl: $imageUrl) { id name style }
  }
`;
export const UPDATE_DECORATION_THEME_MUTATION = gql`
  mutation UpdateDecorationTheme($id: ID!, $name: String, $style: String, $colorPalette: [String], $description: String, $estimatedCost: Float, $imageUrl: String, $isActive: Boolean) {
    updateDecorationTheme(id: $id, name: $name, style: $style, colorPalette: $colorPalette, description: $description, estimatedCost: $estimatedCost, imageUrl: $imageUrl, isActive: $isActive) { id name isActive }
  }
`;
export const DELETE_DECORATION_THEME_MUTATION = gql`
  mutation DeleteDecorationTheme($id: ID!) { deleteDecorationTheme(id: $id) }
`;

// Phase 1 — Wishlist Templates (admin CRUD)
export const ALL_WISHLIST_TEMPLATES_QUERY = gql`
  query AllWishlistTemplates {
    allWishlistTemplates { id name tradition eventScale priority items isActive }
  }
`;
export const CREATE_WISHLIST_TEMPLATE_MUTATION = gql`
  mutation CreateWishListTemplate($name: String!, $tradition: String, $eventScale: String, $priority: String, $items: [JSON], $isActive: Boolean) {
    createWishListTemplate(name: $name, tradition: $tradition, eventScale: $eventScale, priority: $priority, items: $items, isActive: $isActive) { id name tradition }
  }
`;
export const UPDATE_WISHLIST_TEMPLATE_MUTATION = gql`
  mutation UpdateWishListTemplate($id: ID!, $name: String, $tradition: String, $eventScale: String, $priority: String, $items: [JSON], $isActive: Boolean) {
    updateWishListTemplate(id: $id, name: $name, tradition: $tradition, eventScale: $eventScale, priority: $priority, items: $items, isActive: $isActive) { id name tradition isActive }
  }
`;
export const DELETE_WISHLIST_TEMPLATE_MUTATION = gql`
  mutation DeleteWishListTemplate($id: ID!) { deleteWishListTemplate(id: $id) }
`;

export const ADMIN_ALL_CEREMONIES_QUERY = gql`
  query AdminAllCeremonies {
    allCeremonies {
      id name ceremonyType eventDate
      user { id email profile { firstName lastName } }
    }
  }
`;

// Phase 3 — Admin global views
// Services a bride/groom family chose (contracts + Service Wizard wishlists); drives their menu
export const MY_SELECTED_SERVICE_CATEGORIES_QUERY = gql`
  query MySelectedServiceCategories {
    mySelectedServiceCategories
  }
`;
export const ADMIN_ALL_STAFFING_QUERY = gql`
  query AdminAllStaffingRequirements {
    allStaffingRequirements {
      id ceremonyId roleName requiredCount confirmedCount status notes
      ceremony { id name ceremonyType }
      vendor { id name }
      owner { id email profile { firstName lastName } }
    }
  }
`;
export const ADMIN_ALL_PARKING_QUERY = gql`
  query AdminAllParkingSlots {
    allParkingSlots {
      id ceremonyId zoneName slotNumber slotType status notes
      ceremony { id name ceremonyType }
      owner { id email profile { firstName lastName } }
    }
  }
`;
export const ADMIN_ALL_TRANSPORT_QUERY = gql`
  query AdminAllTransportVehicles {
    allTransportVehicles {
      id vehicleNumber vehicleType capacity driverName driverPhone status notes
      vendor { id name }
      owner { id email profile { firstName lastName } }
    }
  }
`;

// Phase 4 — Admin global views
export const ADMIN_ALL_TIMELINE_EVENTS_QUERY = gql`
  query AdminAllTimelineEvents {
    allTimelineEvents {
      id title description eventType startTime endTime allDay status assignedTo color sortOrder
      ceremony { id name ceremonyType }
    }
  }
`;
export const ADMIN_ALL_BUDGET_CATEGORIES_QUERY = gql`
  query AdminAllBudgetCategories {
    allBudgetCategories {
      id name description plannedAmount color sortOrder totalPlanned totalActual totalPaid
      serviceCategory fromService budgetAmount hasOwnBudget remaining usedPercent status
      creator { id email role { name displayName } }
      owner { id email profile { firstName lastName } }
    }
  }
`;

// Admin > Budgets: each bride/groom family's budget at a glance
export const BUDGET_OVERVIEW_QUERY = gql`
  query BudgetOverview {
    budgetOverview {
      familyUserId familyName email roleName totalBudget totalSpent totalPaid usedPercent status
      categoryCount nearCount overCount overdueCount
    }
  }
`;
export const ADMIN_ALL_NOTIFICATIONS_QUERY = gql`
  query AdminAllNotifications {
    allNotifications {
      id userId type title message isRead scheduledAt sentAt createdAt
      creator { id email }
    }
  }
`;

// ── Email Settings & Templates (Admin) ────────────────────────
export const EMAIL_SETTINGS_QUERY = gql`
  query EmailSettings {
    emailSettings {
      id smtpHost smtpPort smtpSecure smtpUser smtpPassword fromName fromEmail replyToEmail isActive createdAt updatedAt
    }
  }
`;

export const SAVE_EMAIL_SETTINGS_MUTATION = gql`
  mutation SaveEmailSettings($smtpHost: String! $smtpPort: Int! $smtpSecure: Boolean! $smtpUser: String! $smtpPassword: String! $fromName: String! $fromEmail: String! $replyToEmail: String) {
    saveEmailSettings(smtpHost: $smtpHost smtpPort: $smtpPort smtpSecure: $smtpSecure smtpUser: $smtpUser smtpPassword: $smtpPassword fromName: $fromName fromEmail: $fromEmail replyToEmail: $replyToEmail) {
      id smtpHost smtpPort smtpSecure smtpUser fromName fromEmail replyToEmail isActive
    }
  }
`;

export const TEST_EMAIL_SETTINGS_MUTATION = gql`
  mutation TestEmailSettings($recipientEmail: String!) {
    testEmailSettings(recipientEmail: $recipientEmail) { success error }
  }
`;

export const ALL_EMAIL_TEMPLATES_QUERY = gql`
  query AllEmailTemplates {
    allEmailTemplates {
      id name subject body description variables isActive createdAt updatedAt
    }
  }
`;

export const CREATE_EMAIL_TEMPLATE_MUTATION = gql`
  mutation CreateEmailTemplate($name: String! $subject: String! $body: String! $description: String $variables: [String] $isActive: Boolean) {
    createEmailTemplate(name: $name subject: $subject body: $body description: $description variables: $variables isActive: $isActive) {
      id name subject body description variables isActive
    }
  }
`;

export const UPDATE_EMAIL_TEMPLATE_MUTATION = gql`
  mutation UpdateEmailTemplate($id: ID! $name: String $subject: String $body: String $description: String $variables: [String] $isActive: Boolean) {
    updateEmailTemplate(id: $id name: $name subject: $subject body: $body description: $description variables: $variables isActive: $isActive) {
      id name subject body description variables isActive
    }
  }
`;

export const DELETE_EMAIL_TEMPLATE_MUTATION = gql`
  mutation DeleteEmailTemplate($id: ID!) { deleteEmailTemplate(id: $id) }
`;

export const ALL_NOTIFICATION_TRIGGERS_QUERY = gql`
  query AllNotificationTriggers {
    allNotificationTriggers {
      id name eventType templateId description isActive sendEmail sendInApp recipientType createdAt updatedAt
      template { id name subject }
    }
  }
`;

export const CREATE_NOTIFICATION_TRIGGER_MUTATION = gql`
  mutation CreateNotificationTrigger($name: String! $eventType: String! $templateId: ID $description: String $isActive: Boolean $sendEmail: Boolean $sendInApp: Boolean $recipientType: String) {
    createNotificationTrigger(name: $name eventType: $eventType templateId: $templateId description: $description isActive: $isActive sendEmail: $sendEmail sendInApp: $sendInApp recipientType: $recipientType) {
      id name eventType templateId description isActive sendEmail sendInApp recipientType
      template { id name subject }
    }
  }
`;

export const UPDATE_NOTIFICATION_TRIGGER_MUTATION = gql`
  mutation UpdateNotificationTrigger($id: ID! $name: String $eventType: String $templateId: ID $description: String $isActive: Boolean $sendEmail: Boolean $sendInApp: Boolean $recipientType: String) {
    updateNotificationTrigger(id: $id name: $name eventType: $eventType templateId: $templateId description: $description isActive: $isActive sendEmail: $sendEmail sendInApp: $sendInApp recipientType: $recipientType) {
      id name eventType templateId description isActive sendEmail sendInApp recipientType
      template { id name subject }
    }
  }
`;

export const DELETE_NOTIFICATION_TRIGGER_MUTATION = gql`
  mutation DeleteNotificationTrigger($id: ID!) { deleteNotificationTrigger(id: $id) }
`;

export const EMAIL_LOGS_QUERY = gql`
  query EmailLogs($limit: Int $status: String) {
    emailLogs(limit: $limit status: $status) {
      id recipientEmail subject status errorMessage sentAt createdAt
      template { id name }
      trigger { id name }
    }
  }
`;


// ── Vendor service offerings ──────────────────────────────────────────────
const VENDOR_OFFERING_FIELDS = `
  id vendorUserId vendorEmail category categoryName title description businessName
  city serviceArea priceFrom priceTo priceUnit contactName contactPhone contactEmail cancellationPolicy
  details isActive isPublished visibilityNote createdAt updatedAt
  packages { id name description pricing price priceUnit ceremonyType items { id section name description price photos } }
`;

export const VENDOR_OFFERING_FIELD_DEFS_QUERY = gql`
  query VendorOfferingFields {
    vendorOfferingFields
  }
`;

export const MY_VENDOR_SERVICE_CATEGORIES_QUERY = gql`
  query MyVendorServiceCategories {
    myVendorServiceCategories { category displayName contractId contractRef contractTitle contractStatus isSigned isAccepted isVendorRequest vendorSigned }
  }
`;

export const MY_VENDOR_OFFERINGS_QUERY = gql`
  query MyVendorOfferings {
    myVendorOfferings { ${VENDOR_OFFERING_FIELDS} }
  }
`;

export const MY_BOOKABLE_SERVICES_QUERY = gql`
  query MyBookableServices {
    myBookableServices { categories awaitingSignature awaitingApproval awaitingAcceptance }
  }
`;

export const VENDOR_OFFERINGS_QUERY = gql`
  query VendorOfferings($category: String, $city: String, $search: String, $includeUnpublished: Boolean) {
    vendorOfferings(category: $category, city: $city, search: $search, includeUnpublished: $includeUnpublished) { ${VENDOR_OFFERING_FIELDS} }
  }
`;

// Service Providers: runs as the selected family (for admins/matchmakers), so the list is
// limited to the services on that family's signed contract. near/radiusKm: the function place
export const FAMILY_VENDOR_OFFERINGS_QUERY = gql`
  query FamilyVendorOfferings($category: String, $city: String, $search: String, $includeUnpublished: Boolean, $near: String, $radiusKm: Int) {
    vendorOfferings(category: $category, city: $city, search: $search, includeUnpublished: $includeUnpublished, near: $near, radiusKm: $radiusKm) {
      ${VENDOR_OFFERING_FIELDS} distanceKm servesEventCity
    }
  }
`;

export const KNOWN_CITIES_QUERY = gql`
  query KnownCities { knownCities }
`;

// Service Wizard: anonymous vendors by distance from the function place
export const NEARBY_VENDORS_QUERY = gql`
  query NearbyVendors($category: String!, $eventCity: String, $radiusKm: Int, $budget: Float, $guestCount: Int, $preferences: [String!], $keywords: [String!], $sort: String) {
    nearbyVendors(category: $category, eventCity: $eventCity, radiusKm: $radiusKm, budget: $budget, guestCount: $guestCount, preferences: $preferences, keywords: $keywords, sort: $sort) {
      eventCity cityKnown radiusKm sort listLocked withinCount outsideCount
      within { ...NearbyVendorFields }
      outside { ...NearbyVendorFields }
      pick { offeringId title city distanceKm packageId packageName price priceUnit reasons }
    }
  }
  fragment NearbyVendorFields on NearbyVendor {
    offeringId title city serviceArea distanceKm servesEventCity withinRadius
    priceFrom priceTo priceUnit cheapestPrice budgetFit guestsOnBudget matches highlights
    packages { id name description price priceUnit itemCount photos { url caption } sections { name count items } }
  }
`;

export const SERVICE_ESTIMATES_QUERY = gql`
  query ServiceEstimates($guestCount: Int, $eventCity: String) {
    serviceEstimates(guestCount: $guestCount, eventCity: $eventCity) {
      category source vendorCount nearby perGuest priceUnit unitPriceFrom minCost typicalCost priority
    }
  }
`;

const PREFERRED_VENDOR_FIELDS = 'category categoryName offeringId packageId packageName title city auto businessName available';

export const MY_PREFERRED_VENDORS_QUERY = gql`
  query MyPreferredVendors {
    myPreferredVendors { ${PREFERRED_VENDOR_FIELDS} }
  }
`;

export const CREATE_VENDOR_OFFERING_MUTATION = gql`
  mutation CreateVendorOffering($input: VendorOfferingInput!) {
    createVendorOffering(input: $input) { ${VENDOR_OFFERING_FIELDS} }
  }
`;

export const UPDATE_VENDOR_OFFERING_MUTATION = gql`
  mutation UpdateVendorOffering($id: ID!, $input: VendorOfferingInput!) {
    updateVendorOffering(id: $id, input: $input) { ${VENDOR_OFFERING_FIELDS} }
  }
`;

export const SET_VENDOR_OFFERING_ACTIVE_MUTATION = gql`
  mutation SetVendorOfferingActive($id: ID!, $isActive: Boolean!) {
    setVendorOfferingActive(id: $id, isActive: $isActive) { ${VENDOR_OFFERING_FIELDS} }
  }
`;

export const DELETE_VENDOR_OFFERING_MUTATION = gql`
  mutation DeleteVendorOffering($id: ID!) {
    deleteVendorOffering(id: $id)
  }
`;

export const MY_VENDOR_CONTRACTED_SERVICE_IDS_QUERY = gql`
  query MyVendorContractedServiceIds {
    myVendorContractedServiceIds
  }
`;

export const CREATE_VENDOR_SERVICE_CONTRACT_MUTATION = gql`
  mutation CreateVendorServiceContract($serviceIds: [ID!]!, $notes: String) {
    createVendorServiceContract(serviceIds: $serviceIds, notes: $notes) { id refNumber title status }
  }
`;

export const WITHDRAW_VENDOR_SERVICE_CONTRACT_MUTATION = gql`
  mutation WithdrawVendorServiceContract($id: ID!, $category: String) {
    withdrawVendorServiceContract(id: $id, category: $category)
  }
`;

export const REJECT_VENDOR_SERVICE_CONTRACT_MUTATION = gql`
  mutation RejectVendorServiceContract($id: ID!, $reason: String!) {
    rejectVendorServiceContract(id: $id, reason: $reason) { id status reviewNotes }
  }
`;

// Overview dashboard for the logged-in user's role (admin / matchmaker / family / vendor)
export const MY_DASHBOARD_QUERY = gql`
  query MyDashboard {
    myDashboard
  }
`;

// ── Vendor bookings (family selects a vendor for a required date) ─────────
const VENDOR_SELECTION_FIELDS = `
  id familyUserId vendorUserId offeringId category categoryName offeringTitle businessName
  packageId packageName price priceUnit quantity requiredDate requiredTime notes status vendorNote
  respondedAt cancelledAt familyName familyEmail familyRole vendorEmail contactName contactPhone
  contactEmail city daysUntil createdAt updatedAt
  accountManager { userId name email phone roleName source endsOn assignedAt active }
  progress {
    done total nextStage allDone
    stages { key label description requires status confirmedAt late note confirmedBy files { id name kind mimeType url } }
  }
`;

// The family's primary and secondary contacts: every communication goes to both
export const MY_FAMILY_CONTACTS_QUERY = gql`
  query MyFamilyContacts {
    myFamilyContacts { primary { name relation email phone } secondary { name relation email phone } }
  }
`;

export const SET_FAMILY_CONTACTS_MUTATION = gql`
  mutation SetFamilyContacts($input: FamilyContactsInput!, $familyUserId: ID) {
    setFamilyContacts(input: $input, familyUserId: $familyUserId) { primary { name relation email phone } secondary { name relation email phone } }
  }
`;

// The family's account manager: their point of contact (and their vendors') until the function
export const MY_ACCOUNT_MANAGER_QUERY = gql`
  query MyAccountManager {
    myAccountManager { userId name email phone roleName source endsOn assignedAt active }
  }
`;

export const SET_ACCOUNT_MANAGER_MUTATION = gql`
  mutation SetAccountManager($familyUserId: ID!, $managerUserId: ID, $endsOn: String) {
    setAccountManager(familyUserId: $familyUserId, managerUserId: $managerUserId, endsOn: $endsOn) { id accountManager { userId name email phone roleName source endsOn assignedAt active } }
  }
`;

export const SET_CONTACT_PHONE_MUTATION = gql`
  mutation SetContactPhone($phone: String, $userId: ID) {
    setContactPhone(phone: $phone, userId: $userId) { id phone }
  }
`;

// Vendor confirms the next stage of a booking before the function (files from POST /uploads/booking-files)
export const SUBMIT_BOOKING_STAGE_MUTATION = gql`
  mutation SubmitBookingStage($selectionId: ID!, $stage: String!, $note: String, $fileIds: [ID!]) {
    submitBookingStage(selectionId: $selectionId, stage: $stage, note: $note, fileIds: $fileIds) { ${VENDOR_SELECTION_FIELDS} }
  }
`;

export const MY_VENDOR_SELECTIONS_QUERY = gql`
  query MyVendorSelections($includeClosed: Boolean) {
    myVendorSelections(includeClosed: $includeClosed) { ${VENDOR_SELECTION_FIELDS} }
  }
`;

export const VENDOR_BOOKINGS_QUERY = gql`
  query VendorBookings($status: String) {
    vendorBookings(status: $status) { ${VENDOR_SELECTION_FIELDS} }
  }
`;

export const CREATE_VENDOR_SELECTION_MUTATION = gql`
  mutation CreateVendorSelection($input: VendorSelectionInput!) {
    createVendorSelection(input: $input) { ${VENDOR_SELECTION_FIELDS} }
  }
`;

export const UPDATE_VENDOR_SELECTION_MUTATION = gql`
  mutation UpdateVendorSelection($id: ID!, $input: VendorSelectionUpdateInput!) {
    updateVendorSelection(id: $id, input: $input) { ${VENDOR_SELECTION_FIELDS} }
  }
`;

export const CANCEL_VENDOR_SELECTION_MUTATION = gql`
  mutation CancelVendorSelection($id: ID!, $reason: String) {
    cancelVendorSelection(id: $id, reason: $reason) { ${VENDOR_SELECTION_FIELDS} }
  }
`;

export const RESPOND_TO_VENDOR_SELECTION_MUTATION = gql`
  mutation RespondToVendorSelection($id: ID!, $response: String!, $note: String) {
    respondToVendorSelection(id: $id, response: $response, note: $note) { ${VENDOR_SELECTION_FIELDS} }
  }
`;

export const COMPLETE_VENDOR_SELECTION_MUTATION = gql`
  mutation CompleteVendorSelection($id: ID!) {
    completeVendorSelection(id: $id) { ${VENDOR_SELECTION_FIELDS} }
  }
`;

// Admin > Vendors: vendor user accounts with their services
export const VENDOR_ACCOUNTS_QUERY = gql`
  query VendorAccounts {
    vendorAccounts {
      id email isActive businessName offerings activeOfferings openBookings tempPasswordActive verificationStatus documentsPending documentsApproved createdAt
      services { category label contractStatus contractRef live }
    }
  }
`;

export const REMOVE_VENDOR_SERVICE_MUTATION = gql`
  mutation RemoveVendorService($vendorUserId: ID!, $category: String!) {
    removeVendorService(vendorUserId: $vendorUserId, category: $category)
  }
`;

// ── Admin > Audit Logs ──────────────────────────────────────────────────────
export const AUDIT_LOGS_QUERY = gql`
  query AuditLogs($filter: AuditLogFilter, $limit: Int, $offset: Int) {
    auditLogs(filter: $filter, limit: $limit, offset: $offset) {
      total
      items {
        id occurredAt actorUserId actorEmail actorRole actingForUserId actingForEmail action operation
        entityType entityId changes details success error ipAddress userAgent requestId
      }
    }
  }
`;
export const AUDIT_LOG_FACETS_QUERY = gql`
  query AuditLogFacets { auditLogFacets { actions entityTypes } }
`;

// ── Legal documents (Terms, Privacy Policy, Vendor Agreement...) ───────────
const LEGAL_DOCUMENT_FIELDS = 'key version title summary body effectiveDate requiresAcceptance audience publishedAt reason';
export const LEGAL_DOCUMENTS_QUERY = gql`
  query LegalDocuments { legalDocuments { key version title summary effectiveDate requiresAcceptance audience } }
`;
export const LEGAL_DOCUMENT_QUERY = gql`
  query LegalDocument($key: String!, $version: Int) { legalDocument(key: $key, version: $version) { ${LEGAL_DOCUMENT_FIELDS} } }
`;
export const MY_PENDING_LEGAL_QUERY = gql`
  query MyPendingLegalDocuments { myPendingLegalDocuments { key version title summary body effectiveDate reason } }
`;
export const ACCEPT_LEGAL_MUTATION = gql`
  mutation AcceptLegalDocuments($keys: [String!]!, $context: String, $readInFull: [String!]) {
    acceptLegalDocuments(keys: $keys, context: $context, readInFull: $readInFull) { key version }
  }
`;
const LEGAL_SETTINGS_FIELDS = `grievanceOfficerName grievanceOfficerDesignation grievanceOfficerEmail grievanceOfficerPhone
  grievanceOfficerAddress dataProtectionEmail jurisdictionCity arbitrationSeat missing`;
export const LEGAL_SETTINGS_QUERY = gql`
  query LegalSettings { legalSettings { ${LEGAL_SETTINGS_FIELDS} } }
`;
export const SAVE_LEGAL_SETTINGS_MUTATION = gql`
  mutation SaveLegalSettings($input: LegalSettingsInput!) { saveLegalSettings(input: $input) { ${LEGAL_SETTINGS_FIELDS} } }
`;
export const LEGAL_DOCUMENT_VERSIONS_QUERY = gql`
  query LegalDocumentVersions($key: String!) { legalDocumentVersions(key: $key) { ${LEGAL_DOCUMENT_FIELDS} } }
`;
export const PUBLISH_LEGAL_DOCUMENT_MUTATION = gql`
  mutation PublishLegalDocument($input: LegalDocumentInput!) { publishLegalDocument(input: $input) { key version } }
`;
export const LEGAL_ACCEPTANCE_STATS_QUERY = gql`
  query LegalAcceptanceStats { legalAcceptanceStats { key version title accepted lastAcceptedAt } }
`;

// ── Compliance: complaints, verification, data rights ──────────────────────
const GRIEVANCE_FIELDS = `id ticketNumber name email phone userId category subject description relatedType relatedId status
  createdAt ackDueAt resolveDueAt acknowledgedAt resolvedAt resolution overdue timeline { at status message public }`;
export const SUBMIT_GRIEVANCE_MUTATION = gql`
  mutation SubmitGrievance($input: GrievanceInput!) { submitGrievance(input: $input) { ${GRIEVANCE_FIELDS} } }
`;
export const GRIEVANCE_STATUS_QUERY = gql`
  query GrievanceStatus($ticketNumber: String!, $email: String!) { grievanceStatus(ticketNumber: $ticketNumber, email: $email) { ${GRIEVANCE_FIELDS} } }
`;
export const MY_GRIEVANCES_QUERY = gql`
  query MyGrievances { myGrievances { ${GRIEVANCE_FIELDS} } }
`;
export const GRIEVANCES_QUERY = gql`
  query Grievances($status: String, $category: String, $overdueOnly: Boolean) { grievances(status: $status, category: $category, overdueOnly: $overdueOnly) { ${GRIEVANCE_FIELDS} } }
`;
export const UPDATE_GRIEVANCE_MUTATION = gql`
  mutation UpdateGrievance($id: ID!, $status: String, $message: String, $isPublic: Boolean) { updateGrievance(id: $id, status: $status, message: $message, isPublic: $isPublic) { ${GRIEVANCE_FIELDS} } }
`;
export const REPORT_PROFILE_MUTATION = gql`
  mutation ReportProfile($userId: ID!, $reason: String!, $details: String) { reportProfile(userId: $userId, reason: $reason, details: $details) { ticketNumber } }
`;
const ID_DOC_FIELDS = 'id docType purpose fileName mimeType status note createdAt url';
const VERIFICATION_FIELDS = `userId email role roleName name businessName status dob gender documents { ${ID_DOC_FIELDS} }`;
export const MY_VERIFICATION_QUERY = gql`
  query MyVerification { myVerification { status note verifiedAt documents { ${ID_DOC_FIELDS} } } }
`;
export const PENDING_VERIFICATIONS_QUERY = gql`
  query PendingVerifications { pendingVerifications { ${VERIFICATION_FIELDS} } }
`;
// Admin > Vendors > Documents: one user's documents for review
export const USER_VERIFICATION_QUERY = gql`
  query UserVerification($userId: ID!) { userVerification(userId: $userId) { ${VERIFICATION_FIELDS} } }
`;
// Admin: approve (decision "accepted") or reject one document; returns the account's verification status
export const REVIEW_IDENTITY_DOCUMENT_MUTATION = gql`
  mutation ReviewIdentityDocument($id: ID!, $decision: String!, $note: String) { reviewIdentityDocument(id: $id, decision: $decision, note: $note) }
`;
export const REVIEW_VERIFICATION_MUTATION = gql`
  mutation ReviewVerification($userId: ID!, $decision: String!, $note: String) { reviewVerification(userId: $userId, decision: $decision, note: $note) }
`;
export const MY_DATA_EXPORT_QUERY = gql`
  query MyDataExport { myDataExport }
`;
const DATA_REQUEST_FIELDS = 'id userId email type details status note createdAt completedAt';
export const MY_DATA_REQUESTS_QUERY = gql`
  query MyDataRequests { myDataRequests { ${DATA_REQUEST_FIELDS} } }
`;
export const DATA_REQUESTS_QUERY = gql`
  query DataRequests($status: String) { dataRequests(status: $status) { ${DATA_REQUEST_FIELDS} } }
`;
export const REQUEST_DATA_ERASURE_MUTATION = gql`
  mutation RequestDataErasure($reason: String) { requestDataErasure(reason: $reason) }
`;
export const REQUEST_DATA_CORRECTION_MUTATION = gql`
  mutation RequestDataCorrection($details: String!) { requestDataCorrection(details: $details) }
`;
export const COMPLETE_DATA_REQUEST_MUTATION = gql`
  mutation CompleteDataRequest($id: ID!, $action: String!, $note: String) { completeDataRequest(id: $id, action: $action, note: $note) }
`;
export const MY_NOMINEE_QUERY = gql`
  query MyNominee { myNominee { name relation email phone } }
`;
export const SET_NOMINEE_MUTATION = gql`
  mutation SetNominee($input: NomineeInput!) { setNominee(input: $input) { name relation email phone } }
`;
export const COMPLIANCE_OVERVIEW_QUERY = gql`
  query ComplianceOverview {
    complianceOverview {
      missingLegalDetails grievancesOpen grievancesAckOverdue grievancesResolveOverdue safetyReportsOpen
      verificationsPending familiesVerified familiesTotal dataRequestsPending oldestDataRequestAt
      offeringsMissingCancellation profilesUndeclared usersAcceptedTerms activeUsers
    }
  }
`;

// ── Admin > Marketing: vendor leads ───────────────────────────────────────────
export const VENDOR_LEAD_FIELDS = `
  id name alternateName contactPerson services categoryRaw typeRaw state district city area address pincode
  phone phones phoneRaw hasMobile email website facebook rating reviews startingPrice about premium listingUrl mapsLink
  source status notes contactCount lastContactedAt acceptedAt acceptedEmail convertedUserId convertedAt inviteLink tempPasswordActive vendorState createdAt
`;
export const VENDOR_LEADS_QUERY = gql`
  query VendorLeads($filter: VendorLeadFilter, $limit: Int, $offset: Int, $sort: String) {
    vendorLeads(filter: $filter, limit: $limit, offset: $offset, sort: $sort) { total rows { ${VENDOR_LEAD_FIELDS} } }
  }
`;
export const VENDOR_LEAD_STATS_QUERY = gql`
  query VendorLeadStats { vendorLeadStats marketingSettings { sendingEnabled sendingLocked sendingUpdatedAt sendingUpdatedBy smsConfigured appUrl appUrlIsLocal template } }
`;
export const VENDOR_LEAD_IMPORTS_QUERY = gql`
  query VendorLeadImports { vendorLeadImports { id fileName totalRows inserted updated skipped summary importedBy createdAt } }
`;
export const VENDOR_LEAD_OUTREACH_QUERY = gql`
  query VendorLeadOutreach($leadId: ID!) { vendorLeadOutreach(leadId: $leadId) { id purpose channel recipient status subject message error sentBy createdAt } }
`;
export const IMPORT_VENDOR_LEADS_MUTATION = gql`
  mutation ImportVendorLeads($csv: String!, $fileName: String) { importVendorLeads(csv: $csv, fileName: $fileName) }
`;
export const SEND_VENDOR_OUTREACH_MUTATION = gql`
  mutation SendVendorOutreach($ids: [ID!], $filter: VendorLeadFilter, $channels: [String!]!, $subject: String, $message: String, $templates: JSON) {
    sendVendorOutreach(ids: $ids, filter: $filter, channels: $channels, subject: $subject, message: $message, templates: $templates)
  }
`;
export const SET_VENDOR_LEAD_STATUS_MUTATION = gql`
  mutation SetVendorLeadStatus($ids: [ID!]!, $status: String!, $note: String) { setVendorLeadStatus(ids: $ids, status: $status, note: $note) }
`;
export const VENDOR_LEAD_INVITE_LINK_MUTATION = gql`
  mutation VendorLeadInviteLink($id: ID!) { vendorLeadInviteLink(id: $id) }
`;
export const CONVERT_VENDOR_LEAD_MUTATION = gql`
  mutation ConvertVendorLead($id: ID!, $email: String, $phone: String, $contactPerson: String) {
    convertVendorLead(id: $id, email: $email, phone: $phone, contactPerson: $contactPerson) { leadId userId email tempPassword alreadyConverted emailStatus }
  }
`;
export const VENDOR_LEAD_CREDENTIALS_QUERY = gql`
  query VendorLeadCredentials($leadId: ID!) { vendorLeadCredentials(leadId: $leadId) { leadId userId email tempPassword status issuedAt viewCount } }
`;
export const VENDOR_CREDENTIALS_QUERY = gql`
  query VendorCredentials($vendorUserId: ID!) { vendorCredentials(vendorUserId: $vendorUserId) { userId email tempPassword status issuedAt viewCount } }
`;
export const SET_VENDOR_PASSWORD_MUTATION = gql`
  mutation SetVendorPassword($vendorUserId: ID!, $password: String, $generate: Boolean) {
    setVendorPassword(vendorUserId: $vendorUserId, password: $password, generate: $generate) { userId email tempPassword status issuedAt viewCount }
  }
`;
export const VENDOR_INVITE_QUERY = gql`
  query VendorInvite($token: String!) { vendorInvite(token: $token) { name contactPerson city state address services email phone status brandName } }
`;
export const RESPOND_VENDOR_INVITE_MUTATION = gql`
  mutation RespondVendorInvite($token: String!, $input: VendorInviteResponse!) {
    respondVendorInvite(token: $token, input: $input) { status email tempPassword emailStatus }
  }
`;
export const ACCEPT_VENDOR_LEADS_MUTATION = gql`
  mutation AcceptVendorLeads($items: [VendorLeadAccept!]!) {
    acceptVendorLeads(items: $items) { leadId name services userId email tempPassword alreadyConverted emailStatus whatsappStatus smsStatus error }
  }
`;
// Admin > Marketing > Add vendor: a lead entered by hand
export const CREATE_VENDOR_LEAD_MUTATION = gql`
  mutation CreateVendorLead($input: VendorLeadInput!) { createVendorLead(input: $input) { ${VENDOR_LEAD_FIELDS} } }
`;
// A converted lead whose vendor account was deactivated or deleted: set it up again (new login details)
export const RESTORE_VENDOR_LEAD_MUTATION = gql`
  mutation RestoreVendorLead($id: ID!, $email: String, $phone: String, $contactPerson: String) {
    restoreVendorLead(id: $id, email: $email, phone: $phone, contactPerson: $contactPerson) {
      leadId name services userId email tempPassword reactivated emailStatus whatsappStatus smsStatus
    }
  }
`;

// ── Admin > Messaging Templates (WhatsApp synced with Meta, SMS DLT, email) ─────
export const MESSAGE_TEMPLATE_FIELDS = `
  id channel name language category purpose status providerId subject headerText headerFormat body footerText buttons variables
  dltTemplateId providerTemplateId senderId qualityScore rejectedReason lastError syncedAt isActive hasLocalChanges sendable createdAt updatedAt
`;
export const MESSAGE_TEMPLATES_QUERY = gql`
  query MessageTemplates($channel: String, $purpose: String) { messageTemplates(channel: $channel, purpose: $purpose) { ${MESSAGE_TEMPLATE_FIELDS} } }
`;
export const MESSAGING_SETUP_QUERY = gql`
  query MessagingSetup { messagingSetup }
`;
export const MESSAGE_TEMPLATE_PREVIEW_QUERY = gql`
  query MessageTemplatePreview($id: ID!) { messageTemplatePreview(id: $id) { subject text html warnings } }
`;
export const SAVE_MESSAGE_TEMPLATE_MUTATION = gql`
  mutation SaveMessageTemplate($input: MessageTemplateInput!) { saveMessageTemplate(input: $input) { ${MESSAGE_TEMPLATE_FIELDS} } }
`;
export const DELETE_MESSAGE_TEMPLATE_MUTATION = gql`
  mutation DeleteMessageTemplate($id: ID!) { deleteMessageTemplate(id: $id) }
`;
export const SYNC_WHATSAPP_TEMPLATES_MUTATION = gql`
  mutation SyncWhatsAppTemplates { syncWhatsAppTemplates }
`;
export const SUBMIT_WHATSAPP_TEMPLATE_MUTATION = gql`
  mutation SubmitWhatsAppTemplate($id: ID!) { submitWhatsAppTemplate(id: $id) { ${MESSAGE_TEMPLATE_FIELDS} } }
`;
export const SAVE_WHATSAPP_BUSINESS_ACCOUNT_MUTATION = gql`
  mutation SaveWhatsAppBusinessAccount($wabaId: String) { saveWhatsAppBusinessAccount(wabaId: $wabaId) }
`;
export const IMPORT_DLT_TEMPLATES_MUTATION = gql`
  mutation ImportDltTemplates($csv: String!) { importDltTemplates(csv: $csv) }
`;
export const SAVE_SMS_SETTINGS_MUTATION = gql`
  mutation SaveSmsSettings($input: SmsSettingsInput!) { saveSmsSettings(input: $input) }
`;
export const CHECK_MESSAGING_CONNECTION_MUTATION = gql`
  mutation CheckMessagingConnection($channel: String!) { checkMessagingConnection(channel: $channel) }
`;
export const TEST_MESSAGE_TEMPLATE_MUTATION = gql`
  mutation TestMessageTemplate($id: ID!, $to: String!) { testMessageTemplate(id: $id, to: $to) }
`;

// ── Vendor: what must be entered / uploaded before a services contract ─────────
export const MY_VENDOR_READINESS_QUERY = gql`
  query MyVendorReadiness($categories: [String!]) {
    myVendorReadiness(categories: $categories) {
      ready businessReady documentsApproved minPhotos
      business { key label done hint }
      services { category displayName contracted offeringId offeringTitle ready checks { key label done hint } }
    }
  }
`;

// ── Vendor: upload a service's offerings from its CSV template ────────────────
export const VENDOR_SERVICE_CSV_TEMPLATE_QUERY = gql`
  query VendorServiceCsvTemplate($category: String!) { vendorServiceCsvTemplate(category: $category) }
`;
export const IMPORT_VENDOR_SERVICE_CSV_MUTATION = gql`
  mutation ImportVendorServiceCsv($category: String!, $csv: String!, $dryRun: Boolean) {
    importVendorServiceCsv(category: $category, csv: $csv, dryRun: $dryRun)
  }
`;

// ── Admin > Company Details: marketing messages on / off ──────────────────────
export const MARKETING_SETTINGS_QUERY = gql`
  query MarketingSettings { marketingSettings { sendingEnabled sendingLocked sendingUpdatedAt sendingUpdatedBy smsConfigured appUrl appUrlIsLocal template } }
`;
export const SET_MARKETING_SENDING_MUTATION = gql`
  mutation SetMarketingSending($enabled: Boolean!) { setMarketingSending(enabled: $enabled) { sendingEnabled sendingLocked sendingUpdatedAt sendingUpdatedBy smsConfigured appUrl appUrlIsLocal template } }
`;

// ── Surveys (Admin > Surveys, public /survey/:token) ──────────────────────────
export const SURVEY_SETUP_QUERY = gql`query SurveySetup { surveySetup }`;
export const SURVEY_TEMPLATES_QUERY = gql`query SurveyTemplates { surveyTemplates }`;
export const SURVEYS_QUERY = gql`query Surveys($status: String) { surveys(status: $status) }`;
export const SURVEY_QUERY = gql`query Survey($id: ID!) { survey(id: $id) }`;
export const SURVEY_RESULTS_QUERY = gql`query SurveyResults($id: ID!, $filter: JSON) { surveyResults(id: $id, filter: $filter) }`;
export const SURVEY_INVITATIONS_QUERY = gql`query SurveyInvitations($id: ID!, $status: String, $limit: Int, $offset: Int) { surveyInvitations(id: $id, status: $status, limit: $limit, offset: $offset) }`;
export const SURVEY_RESPONSES_CSV_QUERY = gql`query SurveyResponsesCsv($id: ID!, $filter: JSON) { surveyResponsesCsv(id: $id, filter: $filter) }`;
export const SURVEY_BY_TOKEN_QUERY = gql`query SurveyByToken($token: String!, $via: String) { surveyByToken(token: $token, via: $via) }`;
export const SURVEY_PEOPLE_QUERY = gql`query SurveyPeople($search: String) { surveyPeople(search: $search) }`;
export const SURVEY_PERSON_HISTORY_QUERY = gql`query SurveyPersonHistory($userId: ID!) { surveyPersonHistory(userId: $userId) }`;
export const MY_SURVEYS_QUERY = gql`query MySurveys { mySurveys }`;
export const SAVE_SURVEY_TEMPLATE_MUTATION = gql`mutation SaveSurveyTemplate($input: JSON!) { saveSurveyTemplate(input: $input) }`;
export const DELETE_SURVEY_TEMPLATE_MUTATION = gql`mutation DeleteSurveyTemplate($id: ID!) { deleteSurveyTemplate(id: $id) }`;
export const SAVE_SURVEY_MUTATION = gql`mutation SaveSurvey($input: JSON!) { saveSurvey(input: $input) }`;
export const SET_SURVEY_STATUS_MUTATION = gql`mutation SetSurveyStatus($id: ID!, $status: String!) { setSurveyStatus(id: $id, status: $status) }`;
export const DUPLICATE_SURVEY_MUTATION = gql`mutation DuplicateSurvey($id: ID!) { duplicateSurvey(id: $id) }`;
export const DELETE_SURVEY_MUTATION = gql`mutation DeleteSurvey($id: ID!) { deleteSurvey(id: $id) }`;
export const RUN_SURVEY_NOW_MUTATION = gql`mutation RunSurveyNow($id: ID!) { runSurveyNow(id: $id) }`;
export const SEND_SURVEY_NOW_MUTATION = gql`mutation SendSurveyNow($id: ID!, $userIds: [ID!]) { sendSurveyNow(id: $id, userIds: $userIds) }`;
export const SET_SURVEY_SENDING_MUTATION = gql`mutation SetSurveySending($enabled: Boolean!) { setSurveySending(enabled: $enabled) }`;
export const SUBMIT_SURVEY_RESPONSE_MUTATION = gql`mutation SubmitSurveyResponse($token: String!, $answers: JSON!, $via: String) { submitSurveyResponse(token: $token, answers: $answers, via: $via) }`;

// ── Admin > Users / Vendors: delete an account, message a user ──────────────────
export const USER_DELETION_CHECK_QUERY = gql`
  query UserDeletionCheck($userId: ID!) {
    userDeletionCheck(userId: $userId) {
      userId email name role isActive openBookings activeOfferings leadName blockers
      contracts { id ref title status cost signed createdAt }
    }
  }
`;
export const DELETE_USER_MUTATION = gql`
  mutation DeleteUser($userId: ID!, $closeContracts: Boolean, $reason: String) {
    deleteUser(userId: $userId, closeContracts: $closeContracts, reason: $reason) { userId email contractsClosed bookingsCancelled offeringsHidden leadReopened }
  }
`;
export const USER_CONTACT_QUERY = gql`
  query UserContact($userId: ID!) { userContact(userId: $userId) { userId email phone name role isActive businessName } }
`;
export const SEND_USER_MESSAGE_MUTATION = gql`mutation SendUserMessage($input: UserMessageInput!) { sendUserMessage(input: $input) }`;

// ── Admin > Audit Logs > Messages: every WhatsApp, SMS and email sent ───────────
export const MESSAGE_LOGS_QUERY = gql`
  query MessageLogs($filter: MessageLogFilter, $limit: Int, $offset: Int) {
    messageLogs(filter: $filter, limit: $limit, offset: $offset) {
      total counts
      rows {
        id channel recipient recipientUserId recipientEmail purpose templateId templateName subject body status error provider
        providerMessageId relatedType relatedId sentBy actingFor createdAt
      }
    }
  }
`;

// Sign-up: the documents a new account of the role reads and accepts
export const SIGNUP_LEGAL_DOCUMENTS_QUERY = gql`
  query SignupLegalDocuments($roleName: String!) {
    signupLegalDocuments(roleName: $roleName) { key version title summary body effectiveDate }
  }
`;
